import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  BarChart,
  Bar,
  LineChart,
  Line,
  Cell,
} from "recharts";

/** ======= CONFIG ======= */
const BASE = "/data/nascar";
const META_FILE = "track-metadata.json";
const LOGS_FILE = "NASCAR Loop Data.json";

const TRACK_METADATA_FALLBACKS = [
  {
    track: "Nashville Superspeedway",
    aliases: [
      "Nashville",
      "Nashville Speedway",
      "Nashville SuperSpeedway",
      "Nashville Superspeedway",
    ],
    custom_groups: ["intermediate", "concrete"],
    length_miles: 1.333,
    surface: "Concrete",
    shape: "Tri-oval",
    size_group: "Intermediate",
    is_concrete: true,
    is_road_course: false,
    is_street_course: false,
  },
];

/* ---------------- exhibition races (Cup only) ----------------
   Exhibition status is determined from RaceName instead of hard-coded dates.
   Any Cup race containing Clash, Duel, or All Star / All-Star is exhibition.
*/

/** ======= HELPERS ======= */
async function fetchJson(url) {
  const res = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status} @ ${url}`);
  const txt = await res.text();
  const ct = res.headers.get("content-type") || "";
  const looksJson = ct.includes("application/json") || /^[\[{]/.test(txt.trim());
  if (!looksJson) throw new Error(`Not JSON @ ${url}`);
  try {
    return JSON.parse(txt);
  } catch {
    throw new Error(`Invalid JSON @ ${url}`);
  }
}

function lower(s) {
  return String(s ?? "").toLowerCase();
}

function publicSeriesLabel(value) {
  const s = String(value ?? "");
  return /xfinity/i.test(s) ? s.replace(/xfinity/gi, "O'Reilly") : s;
}

function normTrackKey(s) {
  return String(s ?? "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isCupSeriesName(seriesName) {
  return lower(seriesName).includes("cup");
}

function isExhibitionRace(seriesName, raceName) {
  if (!isCupSeriesName(seriesName)) return false;

  const name = String(raceName ?? "")
    .toLowerCase()
    .replace(/[\u2010-\u2015-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return (
    name.includes("clash") ||
    name.includes("duel") ||
    name.includes("all star")
  );
}

function safeYearFromDate(d) {
  if (!d) return "";
  const s = String(d);
  const y = s.slice(0, 4);
  return /^\d{4}$/.test(y) ? y : "";
}

function toNum(v) {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/[%,$\s,]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function avg(arr) {
  const vals = arr.filter((v) => v != null && Number.isFinite(v));
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function sum(arr) {
  const vals = arr.filter((v) => v != null && Number.isFinite(v));
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0);
}

function median(arr) {
  const vals = arr.filter((v) => v != null && Number.isFinite(v)).sort((a, b) => a - b);
  if (!vals.length) return null;
  const mid = Math.floor(vals.length / 2);
  return vals.length % 2 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2;
}

function stdDev(arr) {
  const vals = arr.filter((v) => v != null && Number.isFinite(v));
  if (vals.length < 2) return null;
  const mean = avg(vals);
  const variance = vals.reduce((acc, v) => acc + (v - mean) ** 2, 0) / vals.length;
  return Math.sqrt(variance);
}

function corr(xs, ys) {
  const pairs = xs
    .map((x, i) => [x, ys[i]])
    .filter(([x, y]) => x != null && y != null && Number.isFinite(x) && Number.isFinite(y));

  if (pairs.length < 3) return null;

  const xVals = pairs.map(([x]) => x);
  const yVals = pairs.map(([, y]) => y);

  const xMean = avg(xVals);
  const yMean = avg(yVals);

  let num = 0;
  let xDen = 0;
  let yDen = 0;

  for (let i = 0; i < pairs.length; i++) {
    const dx = xVals[i] - xMean;
    const dy = yVals[i] - yMean;
    num += dx * dy;
    xDen += dx * dx;
    yDen += dy * dy;
  }

  if (!xDen || !yDen) return null;
  return num / Math.sqrt(xDen * yDen);
}

function r2FromCorr(r) {
  if (r == null || !Number.isFinite(r)) return null;
  return r * r;
}

function absFinite(v) {
  return v == null || !Number.isFinite(v) ? null : Math.abs(v);
}

function linearRegression(xs, ys) {
  const pairs = xs
    .map((x, i) => [x, ys[i]])
    .filter(([x, y]) => x != null && y != null && Number.isFinite(x) && Number.isFinite(y));

  if (pairs.length < 2) return null;

  const xVals = pairs.map(([x]) => x);
  const yVals = pairs.map(([, y]) => y);
  const xMean = avg(xVals);
  const yMean = avg(yVals);

  let num = 0;
  let den = 0;
  for (let i = 0; i < pairs.length; i++) {
    const dx = xVals[i] - xMean;
    num += dx * (yVals[i] - yMean);
    den += dx * dx;
  }
  if (!den) return null;

  const slope = num / den;
  const intercept = yMean - slope * xMean;

  return { slope, intercept };
}

function buildHistogram(values, bins = 12) {
  const vals = values.filter((v) => v != null && Number.isFinite(v));
  if (!vals.length) return [];
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  if (min === max) {
    return [{ binLabel: fmt(min, 1), binStart: min, binEnd: max, count: vals.length }];
  }

  const width = (max - min) / bins;
  const out = Array.from({ length: bins }, (_, i) => {
    const start = min + i * width;
    const end = i === bins - 1 ? max : start + width;
    return {
      binLabel: `${fmt(start, 1)}-${fmt(end, 1)}`,
      binStart: start,
      binEnd: end,
      count: 0,
    };
  });

  for (const v of vals) {
    let idx = Math.floor((v - min) / width);
    if (idx >= bins) idx = bins - 1;
    if (idx < 0) idx = 0;
    out[idx].count += 1;
  }

  return out;
}

function fmt(v, digits = 2) {
  if (v == null || v === "") return "—";
  if (typeof v === "number") {
    const rounded = Math.round(v * 10 ** digits) / 10 ** digits;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(digits);
  }
  return String(v);
}

function fmtPct(v, digits = 1) {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${(v * 100).toFixed(digits)}%`;
}

function fmtSigned(v, digits = 2) {
  if (v == null || !Number.isFinite(v)) return "—";
  const s = v > 0 ? "+" : "";
  return `${s}${v.toFixed(digits)}`;
}

function prettyBool(v) {
  if (v == null) return "—";
  return v ? "Yes" : "No";
}

function pickCol(allCols, candidates) {
  const map = new Map(allCols.map((c) => [lower(String(c).trim()), c]));
  for (const cand of candidates) {
    const hit = map.get(lower(String(cand).trim()));
    if (hit) return hit;
  }
  for (const cand of candidates) {
    const hit = allCols.find((c) => lower(String(c)).includes(lower(String(cand))));
    if (hit) return hit;
  }
  return "";
}

function buildRaceKey(row, col) {
  const d = col.date ? String(row?.[col.date] ?? "") : "";
  const s = col.series ? String(row?.[col.series] ?? "") : "";
  const t = col.track ? String(row?.[col.track] ?? "") : "";
  const r = col.race ? String(row?.[col.race] ?? "") : "";
  return [s, d, t, r].filter(Boolean).join("|");
}

function bucketStart(pos) {
  const n = toNum(pos);
  if (n == null) return "Unknown";
  if (n <= 2) return "1-2";
  if (n <= 5) return "3-5";
  if (n <= 10) return "6-10";
  if (n <= 15) return "11-15";
  if (n <= 20) return "16-20";
  if (n <= 25) return "21-25";
  return "26+";
}

function clamp(v, lo = 0, hi = 1) {
  if (v == null || !Number.isFinite(v)) return null;
  return Math.max(lo, Math.min(hi, v));
}

function normalizedSimilarity(a, b, range) {
  if (a == null || b == null || !Number.isFinite(a) || !Number.isFinite(b) || !range) return null;
  return clamp(1 - Math.abs(a - b) / range);
}

function exactSimilarity(a, b) {
  if (a == null || b == null || a === "" || b === "") return null;
  return a === b ? 1 : 0;
}

function setSimilarity(a, b) {
  const A = new Set(Array.isArray(a) ? a : []);
  const B = new Set(Array.isArray(b) ? b : []);
  if (!A.size || !B.size) return null;
  const inter = [...A].filter((x) => B.has(x)).length;
  const union = new Set([...A, ...B]).size || 1;
  return inter / union;
}

function getTrackType(meta) {
  const groups = (meta?.custom_groups || []).map((x) => lower(x));
  const length = toNum(meta?.length_miles);
  const road = !!meta?.is_road_course;
  const street = !!meta?.is_street_course;

  if (road || street || groups.some((g) => g.includes("road") || g.includes("street"))) return "road";
  if (groups.some((g) => g.includes("superspeedway")) || (length != null && length >= 2.2)) return "superspeedway";
  if (groups.some((g) => g.includes("short flat"))) return "short_flat";
  if (groups.some((g) => g.includes("short")) || (length != null && length < 1)) return "short";
  if (groups.some((g) => g.includes("intermediate")) || (length != null && length >= 1 && length < 2)) return "intermediate";
  return "other";
}

function getBanking(meta) {
  const t1 = toNum(meta?.turn1_banking_deg);
  const t3 = toNum(meta?.turn3_banking_deg);
  const front = toNum(meta?.frontstretch_banking_deg);
  const back = toNum(meta?.backstretch_banking_deg);
  const values = [t1, t3, front, back].filter((x) => x != null);
  if (!values.length) return { avg: null, text: "—" };
  const avgBank = avg(values);
  return {
    avg: avgBank,
    text: values.length >= 2 ? `${fmt(avgBank, 1)}° avg` : `${fmt(values[0], 1)}°`,
  };
}

function percentileWithin(values, value) {
  const clean = (values || []).filter((v) => v != null && Number.isFinite(v));
  if (!clean.length || value == null || !Number.isFinite(value)) return null;

  let below = 0;
  let equal = 0;

  for (const v of clean) {
    if (v < value) below += 1;
    else if (v === value) equal += 1;
  }

  return ((below + equal * 0.5) / clean.length) * 100;
}

function ordinalPct(pct) {
  if (pct == null || !Number.isFinite(pct)) return "";
  const n = Math.max(0, Math.min(100, Math.round(pct)));
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  const mod10 = n % 10;
  if (mod10 === 1) return `${n}st`;
  if (mod10 === 2) return `${n}nd`;
  if (mod10 === 3) return `${n}rd`;
  return `${n}th`;
}

function percentileLabel(pct, labels) {
  if (pct == null || !Number.isFinite(pct)) return "Not enough data";
  if (pct >= 75) return labels.high;
  if (pct >= 50) return labels.above;
  if (pct >= 25) return labels.below;
  return labels.low;
}

function getTrackFlavor(meta, behavior, comparisonBehaviors = []) {
  const dep = behavior?.track_position_dependency;
  const chaos = behavior?.chaos_score;
  const dom = behavior?.dominator_concentration;

  const depPct = percentileWithin(
    comparisonBehaviors.map((b) => b?.track_position_dependency),
    dep
  );

  const chaosPct = percentileWithin(
    comparisonBehaviors.map((b) => b?.chaos_score),
    chaos
  );

  const domPct = percentileWithin(
    comparisonBehaviors.map((b) => b?.dominator_concentration),
    dom
  );

  const tpBase = percentileLabel(depPct, {
    high: "High Track Position Dependence",
    above: "Above-Average Track Position Dependence",
    below: "Below-Average Track Position Dependence",
    low: "Low Track Position Dependence",
  });

  const chaosBase = percentileLabel(chaosPct, {
    high: "High Chaos",
    above: "Above-Average Chaos",
    below: "Below-Average Chaos",
    low: "Low Chaos",
  });

  const domBase = percentileLabel(domPct, {
    high: "Concentrated Dominator Production",
    above: "Above-Average Dominator Concentration",
    below: "Below-Average Dominator Concentration",
    low: "Spread Dominator Production",
  });

  const tp =
    depPct == null ? tpBase : `${tpBase} • ${ordinalPct(depPct)} percentile`;

  const chaosLabel =
    chaosPct == null ? chaosBase : `${chaosBase} • ${ordinalPct(chaosPct)} percentile`;

  const domLabel =
    domPct == null ? domBase : `${domBase} • ${ordinalPct(domPct)} percentile`;

  return {
    tp,
    chaosLabel,
    domLabel,
    depPct,
    chaosPct,
    domPct,
  };
}

function shareOfTotal(rows, colName, predicate) {
  if (!colName) return null;
  const vals = rows
    .map((r) => ({
      v: toNum(r?.[colName]),
      keep: predicate(r),
    }))
    .filter((x) => x.v != null);

  if (!vals.length) return null;
  const total = sum(vals.map((x) => x.v));
  if (!total) return null;
  const part = sum(vals.filter((x) => x.keep).map((x) => x.v)) || 0;
  return part / total;
}

function raceFieldMetricShare(raceRows, metricCol, startCol, maxStart) {
  if (!metricCol || !startCol) return null;
  const total = sum(raceRows.map((r) => toNum(r?.[metricCol])));
  if (!total) return null;
  const share =
    sum(
      raceRows
        .filter((r) => {
          const s = toNum(r?.[startCol]);
          return s != null && s <= maxStart;
        })
        .map((r) => toNum(r?.[metricCol]))
    ) || 0;
  return share / total;
}

function rankPct(v, thresholds) {
  if (v == null || !Number.isFinite(v)) return "—";
  if (v >= thresholds[2]) return "Elite";
  if (v >= thresholds[1]) return "Strong";
  if (v >= thresholds[0]) return "Moderate";
  return "Low";
}

function getSortableValue(row, key) {
  const v = row?.[key];
  if (v == null) return -Infinity;
  if (typeof v === "number") return v;
  const n = Number(v);
  if (Number.isFinite(n)) return n;
  return String(v).toLowerCase();
}

function sortRows(rows, sortKey, sortDir) {
  if (!sortKey) return rows;
  return [...rows].sort((a, b) => {
    const av = getSortableValue(a, sortKey);
    const bv = getSortableValue(b, sortKey);

    if (typeof av === "string" && typeof bv === "string") {
      const res = av.localeCompare(bv);
      return sortDir === "asc" ? res : -res;
    }

    const res = av < bv ? -1 : av > bv ? 1 : 0;
    return sortDir === "asc" ? res : -res;
  });
}

function scoreTrackSimilarity(baseMeta, compMeta, baseBehavior, compBehavior, mode) {
  let score = 0;
  let total = 0;

  const add = (sim, weight) => {
    if (sim == null || !Number.isFinite(sim)) return;
    score += sim * weight;
    total += weight;
  };

  const baseType = getTrackType(baseMeta);
  const compType = getTrackType(compMeta);

  if (mode === "metadata" || mode === "blended") {
    add(setSimilarity(baseMeta?.custom_groups, compMeta?.custom_groups), 18);
    add(exactSimilarity(baseMeta?.surface, compMeta?.surface), 8);
    add(exactSimilarity(baseMeta?.shape, compMeta?.shape), 8);
    add(exactSimilarity(baseMeta?.size_group, compMeta?.size_group), 9);
    add(normalizedSimilarity(toNum(baseMeta?.length_miles), toNum(compMeta?.length_miles), 2.5), 20);
    add(normalizedSimilarity(getBanking(baseMeta).avg, getBanking(compMeta).avg, 28), 12);
    add(normalizedSimilarity(toNum(baseMeta?.frontstretch_banking_deg), toNum(compMeta?.frontstretch_banking_deg), 18), 4);
    add(normalizedSimilarity(toNum(baseMeta?.backstretch_banking_deg), toNum(compMeta?.backstretch_banking_deg), 18), 4);
    add(exactSimilarity(!!baseMeta?.is_concrete, !!compMeta?.is_concrete), 3);
    add(exactSimilarity(!!baseMeta?.is_high_tire_wear, !!compMeta?.is_high_tire_wear), 5);
    add(exactSimilarity(baseType, compType), 20);
  }

  if ((mode === "behavior" || mode === "blended") && baseBehavior && compBehavior) {
    add(normalizedSimilarity(baseBehavior?.start_finish_corr, compBehavior?.start_finish_corr, 0.9), 14);
    add(normalizedSimilarity(baseBehavior?.avg_abs_pd, compBehavior?.avg_abs_pd, 14), 10);
    add(normalizedSimilarity(baseBehavior?.winner_from_top5_start_pct, compBehavior?.winner_from_top5_start_pct, 1), 10);
    add(normalizedSimilarity(baseBehavior?.top10_start_to_top10_finish_pct, compBehavior?.top10_start_to_top10_finish_pct, 1), 8);
    add(normalizedSimilarity(baseBehavior?.dominator_concentration, compBehavior?.dominator_concentration, 0.7), 10);
    add(normalizedSimilarity(baseBehavior?.fast_lap_concentration, compBehavior?.fast_lap_concentration, 0.7), 8);
    add(normalizedSimilarity(baseBehavior?.track_position_dependency, compBehavior?.track_position_dependency, 0.8), 16);
    add(normalizedSimilarity(baseBehavior?.chaos_score, compBehavior?.chaos_score, 0.8), 12);
    add(normalizedSimilarity(baseBehavior?.front_row_laps_led_share, compBehavior?.front_row_laps_led_share, 0.8), 8);
    add(normalizedSimilarity(baseBehavior?.front_row_fast_lap_share, compBehavior?.front_row_fast_lap_share, 0.8), 8);
  }

  if (!total) return null;

  let finalScore = score / total;

  if (baseType !== compType) {
    if (
      (baseType === "superspeedway" && compType !== "superspeedway") ||
      (compType === "superspeedway" && baseType !== "superspeedway")
    ) {
      finalScore *= 0.58;
    } else if (
      (baseType === "road" && compType !== "road") ||
      (compType === "road" && baseType !== "road")
    ) {
      finalScore *= 0.72;
    } else if (
      (baseType === "short_flat" && compType === "intermediate") ||
      (baseType === "intermediate" && compType === "short_flat")
    ) {
      finalScore *= 0.76;
    } else {
      finalScore *= 0.86;
    }
  }

  const lengthDiff = Math.abs((toNum(baseMeta?.length_miles) ?? 0) - (toNum(compMeta?.length_miles) ?? 0));
  if (lengthDiff > 0.9) finalScore *= 0.86;
  if (lengthDiff > 1.5) finalScore *= 0.74;

  return clamp(finalScore, 0, 1);
}

/** ======= DK / FD SCORING ======= */
function dkFinishPts(finish) {
  const f = Math.round(toNum(finish));
  if (!f || f < 1) return 0;
  if (f === 1) return 45;
  if (f === 2) return 42;
  if (f === 3) return 41;
  if (f === 4) return 40;
  if (f === 5) return 39;
  if (f === 6) return 38;
  if (f === 7) return 37;
  if (f === 8) return 36;
  if (f === 9) return 35;
  if (f === 10) return 34;
  if (f === 11) return 32;
  if (f === 12) return 31;
  if (f === 13) return 30;
  if (f === 14) return 29;
  if (f === 15) return 28;
  if (f === 16) return 27;
  if (f === 17) return 26;
  if (f === 18) return 25;
  if (f === 19) return 24;
  if (f === 20) return 23;
  if (f === 21) return 21;
  if (f === 22) return 20;
  if (f === 23) return 19;
  if (f === 24) return 18;
  if (f === 25) return 17;
  if (f === 26) return 16;
  if (f === 27) return 15;
  if (f === 28) return 14;
  if (f === 29) return 13;
  if (f === 30) return 12;
  if (f === 31) return 10;
  if (f === 32) return 9;
  if (f === 33) return 8;
  if (f === 34) return 7;
  if (f === 35) return 6;
  if (f === 36) return 5;
  if (f === 37) return 4;
  if (f === 38) return 3;
  if (f === 39) return 2;
  if (f === 40) return 1;
  return 0;
}

function fdFinishPts(finish) {
  const f = Math.round(toNum(finish));
  if (!f || f < 1) return 0;
  if (f === 1) return 43;
  if (f === 2) return 40;
  if (f === 3) return 38;
  if (f === 4) return 37;
  if (f === 5) return 36;
  if (f === 6) return 35;
  if (f === 7) return 34;
  if (f === 8) return 33;
  if (f === 9) return 32;
  if (f === 10) return 31;
  if (f === 11) return 30;
  if (f === 12) return 29;
  if (f === 13) return 28;
  if (f === 14) return 27;
  if (f === 15) return 26;
  if (f === 16) return 25;
  if (f === 17) return 24;
  if (f === 18) return 23;
  if (f === 19) return 22;
  if (f === 20) return 21;
  if (f === 21) return 20;
  if (f === 22) return 19;
  if (f === 23) return 18;
  if (f === 24) return 17;
  if (f === 25) return 16;
  if (f === 26) return 15;
  if (f === 27) return 14;
  if (f === 28) return 13;
  if (f === 29) return 12;
  if (f === 30) return 11;
  if (f === 31) return 10;
  if (f === 32) return 9;
  if (f === 33) return 8;
  if (f === 34) return 7;
  if (f === 35) return 6;
  if (f === 36) return 5;
  if (f === 37) return 4;
  if (f === 38) return 3;
  if (f === 39) return 2;
  if (f === 40) return 1;
  return 0;
}

function calcDkPts({ finish, start, fastest_lap, laps_led }) {
  const fin = toNum(finish);
  const st = toNum(start);
  const pd = st && fin ? st - fin : 0;
  return dkFinishPts(fin) + pd * 1 + (toNum(fastest_lap) ?? 0) * 0.45 + (toNum(laps_led) ?? 0) * 0.25;
}

function calcFdPts({ finish, start, laps, laps_led }) {
  const fin = toNum(finish);
  const st = toNum(start);
  const pd = st && fin ? st - fin : 0;
  return fdFinishPts(fin) + pd * 0.5 + (toNum(laps) ?? 0) * 0.1 + (toNum(laps_led) ?? 0) * 0.1;
}

/** ======= UI HELPERS ======= */
function Section({ title, right, children }) {
  return (
    <div className="min-w-0 rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm">
      <div className="flex items-center justify-between gap-3 rounded-t-2xl border-b border-slate-800 bg-slate-900 px-4 py-3">
        <div className="text-sm font-semibold text-slate-100">{title}</div>
        {right ? <div className="text-[11px] text-slate-400">{right}</div> : null}
      </div>
      <div className="min-w-0 p-4">{children}</div>
    </div>
  );
}

function DataPoint({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-800 py-1.5 text-xs last:border-b-0">
      <div className="text-slate-400">{label}</div>
      <div className="font-medium text-slate-100 text-right">{value}</div>
    </div>
  );
}

function CompactStat({ label, value, sub }) {
  return (
    <div className="min-w-0 rounded-xl border border-slate-800 bg-slate-900/95 px-3 py-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-100 leading-none">{value}</div>
      {sub ? <div className="mt-1 text-[11px] text-slate-500">{sub}</div> : null}
    </div>
  );
}

function SortHeader({ label, sortKey, activeSortKey, sortDir, onSort, align = "left" }) {
  const active = sortKey === activeSortKey;
  return (
    <th
      className={`border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-300 ${
        align === "center" ? "text-center" : "text-left"
      }`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 ${align === "center" ? "justify-center w-full" : ""}`}
      >
        <span>{label}</span>
        <span className="text-[10px] text-slate-500">{active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

function GridTable({ children }) {
  return (
    <div className="max-h-[72vh] overflow-auto rounded-xl border border-slate-800">
      <table className="min-w-full border-collapse text-xs">{children}</table>
    </div>
  );
}

function MetricSelect({ label, value, onChange, options, className = "" }) {
  return (
    <div className={`flex flex-col ${className}`}>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 min-w-0 rounded-md border border-slate-700 bg-slate-900 px-2 text-xs text-slate-100 outline-none focus:border-slate-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function RechartsTooltip({ active, payload, label, formatter, labelFormatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-slate-700 bg-slate-950/95 px-3 py-2 text-xs text-slate-100 shadow-xl">
      {labelFormatter ? <div className="mb-1 text-slate-300">{labelFormatter(label, payload)}</div> : null}
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center justify-between gap-4">
          <span className="text-slate-400">{entry.name}</span>
          <span className="font-medium">
            {formatter ? formatter(entry.value, entry.name, entry, i, payload) : fmt(entry.value, 2)}
          </span>
        </div>
      ))}
    </div>
  );
}

/** ======= PAGE ======= */
export default function NascarTrackData() {
  const [metaRows, setMetaRows] = useState([]);
  const [logRows, setLogRows] = useState([]);
  const [updatedAt, setUpdatedAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [series, setSeries] = useState("All");
  const [track, setTrack] = useState("");
  const [trackSearch, setTrackSearch] = useState("");
  const [years, setYears] = useState([]);
  const [excludeExhibition, setExcludeExhibition] = useState(true);
  const [similarityMode, setSimilarityMode] = useState("blended");
  const [expandedRaceKey, setExpandedRaceKey] = useState("");

  const [similarSort, setSimilarSort] = useState({ key: "similarity", dir: "desc" });
  const [bucketSort, setBucketSort] = useState({ key: "sort_order", dir: "asc" });
  const [raceSort, setRaceSort] = useState({ key: "date", dir: "desc" });

  const [mainView, setMainView] = useState("tables");
  const [scatterX, setScatterX] = useState("start");
  const [scatterY, setScatterY] = useState("dk");
  const [histMetric, setHistMetric] = useState("dk");
  const [histBins, setHistBins] = useState(12);
  const [bucketMetric, setBucketMetric] = useState("avg_dk");
  const [trendMetric, setTrendMetric] = useState("avg_dk");
  const [startPosMetric, setStartPosMetric] = useState("avg_dk");
  const [rankingMetric, setRankingMetric] = useState("finish_std");
  const [rankingOrder, setRankingOrder] = useState("desc");
  const [analyticsChartMode, setAnalyticsChartMode] = useState("ranking");
  const [rankingListMode, setRankingListMode] = useState("finish_std");

  useEffect(() => {
    let dead = false;

    (async () => {
      setLoading(true);
      setErr("");
      try {
        const [meta, logs] = await Promise.all([
          fetchJson(`${BASE}/${META_FILE}`),
          fetchJson(`${BASE}/${LOGS_FILE}`),
        ]);

        if (dead) return;

        const metaArr = Array.isArray(meta) ? meta : [];
        const logArr = Array.isArray(logs) ? logs : [];

        setMetaRows(metaArr);
        setLogRows(logArr);

        if (metaArr.length) {
          setTrack(metaArr[0].track || "");
        } else if (TRACK_METADATA_FALLBACKS.length) {
          setTrack(TRACK_METADATA_FALLBACKS[0].track);
        }

        try {
          const u = await fetchJson(`${BASE}/updated.json`);
          if (u?.last_updated) setUpdatedAt(u.last_updated);
        } catch {}
      } catch (e) {
        if (!dead) setErr(String(e?.message || e));
      } finally {
        if (!dead) setLoading(false);
      }
    })();

    return () => {
      dead = true;
    };
  }, []);

  const logCols = useMemo(() => {
    const all = logRows?.[0] ? Object.keys(logRows[0]) : [];
    return {
      date: pickCol(all, ["RaceDate", "date", "race_date"]),
      series: pickCol(all, ["Series", "series", "series_name"]),
      track: pickCol(all, ["Track", "track", "track_name", "venue"]),
      race: pickCol(all, ["RaceName", "race", "race_name", "event", "event_name"]),
      driver: pickCol(all, ["Driver", "driver", "driver_name", "name"]),
      finish: pickCol(all, ["Finish", "finish", "finish_position", "fin"]),
      start: pickCol(all, ["Start", "start", "start_position"]),
      laps_led: pickCol(all, ["LapsLed", "laps_led", "led"]),
      fastest_lap: pickCol(all, ["FastestLaps", "fastest_lap", "fastest_laps", "fast_laps", "fast lap"]),
      laps: pickCol(all, ["LapsCompleted", "laps"]),
      top_15_laps: pickCol(all, ["Top15Laps", "top_15_laps"]),
      green_flag_passing_diff: pickCol(all, ["PassDifferential", "green_flag_passing_diff"]),
      quality_passes: pickCol(all, ["QualityPasses", "quality_passes"]),
      rating: pickCol(all, ["DriverRating", "rating"]),
      avg_run: pickCol(all, ["AvgRunningPos", "avg_run_position", "avg running position", "avg run", "avg"]),
      driver_rating: pickCol(all, ["DriverRating", "driver_rating"]),
      cautions: pickCol(all, ["Cautions", "cautions", "number_of_cautions"]),
      dnf: pickCol(all, ["DNF", "dnf", "dnfs"]),
    };
  }, [logRows]);

  const hasDk = !!(logCols.finish && logCols.start);
  const hasFd = !!(logCols.finish && logCols.start);

  function getDkScore(r) {
    if (!hasDk) return null;
    return calcDkPts({
      finish: logCols.finish ? r?.[logCols.finish] : null,
      start: logCols.start ? r?.[logCols.start] : null,
      fastest_lap: logCols.fastest_lap ? r?.[logCols.fastest_lap] : 0,
      laps_led: logCols.laps_led ? r?.[logCols.laps_led] : 0,
    });
  }

  function getFdScore(r) {
    if (!hasFd) return null;
    return calcFdPts({
      finish: logCols.finish ? r?.[logCols.finish] : null,
      start: logCols.start ? r?.[logCols.start] : null,
      laps: logCols.laps ? r?.[logCols.laps] : 0,
      laps_led: logCols.laps_led ? r?.[logCols.laps_led] : 0,
    });
  }

  const metaTrackOptions = useMemo(() => {
    const seen = new Map();

    const addTrack = (name) => {
      const raw = String(name || "").trim();
      if (!raw) return;

      const key = normTrackKey(raw);
      if (!key || seen.has(key)) return;

      seen.set(key, raw);
    };

    // Tracks from metadata
    for (const m of metaRows) {
      addTrack(m.track);
    }

    // Tracks from actual race logs
    if (logCols.track) {
      for (const r of logRows) {
        addTrack(r?.[logCols.track]);
      }
    }

    // Manual fallbacks for tracks missing from metadata
    for (const m of TRACK_METADATA_FALLBACKS) {
      addTrack(m.track);
    }

    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }, [metaRows, logRows, logCols.track]);

  const filteredTrackOptions = useMemo(() => {
    const q = lower(trackSearch).trim();
    if (!q) return metaTrackOptions;
    return metaTrackOptions.filter((t) => lower(t).includes(q));
  }, [metaTrackOptions, trackSearch]);

  const seriesOptions = useMemo(() => {
    const set = new Set(logRows.map((r) => String(r?.[logCols.series] ?? "")).filter(Boolean));
    return ["All", ...Array.from(set).sort()];
  }, [logRows, logCols.series]);

  const yearOptions = useMemo(() => {
    const set = new Set();
    for (const r of logRows) {
      const y = safeYearFromDate(logCols.date ? r?.[logCols.date] : "");
      if (y) set.add(y);
    }
    return Array.from(set).sort().reverse();
  }, [logRows, logCols.date]);

  const selectedMeta = useMemo(() => {
    const k = normTrackKey(track);

    const metaHit =
      metaRows.find((m) => {
        if (normTrackKey(m.track) === k) return true;
        return Array.isArray(m.aliases) && m.aliases.some((a) => normTrackKey(a) === k);
      }) || null;

    if (metaHit) return metaHit;

    const fallbackHit =
      TRACK_METADATA_FALLBACKS.find((m) => {
        if (normTrackKey(m.track) === k) return true;
        return Array.isArray(m.aliases) && m.aliases.some((a) => normTrackKey(a) === k);
      }) || null;

    if (fallbackHit) return fallbackHit;

    // Last-resort fallback so a log-only track can still be selected
    if (track) {
      return {
        track,
        aliases: [],
        custom_groups: [],
      };
    }

    return null;
  }, [metaRows, track]);

  const selectedTrackKeys = useMemo(() => {
    if (!selectedMeta) return new Set();
    const keys = [selectedMeta.track, ...(selectedMeta.aliases || [])].map(normTrackKey).filter(Boolean);
    return new Set(keys);
  }, [selectedMeta]);

  const filteredLogs = useMemo(() => {
    let out = [...logRows];

    if (series !== "All" && logCols.series) {
      out = out.filter((r) => String(r?.[logCols.series] ?? "") === series);
    }

    if (excludeExhibition && logCols.series && logCols.race) {
      out = out.filter((r) => {
        const s = String(r?.[logCols.series] ?? "");
        const raceName = String(r?.[logCols.race] ?? "");
        return !isExhibitionRace(s, raceName);
      });
    }

    if (selectedTrackKeys.size && logCols.track) {
      out = out.filter((r) => selectedTrackKeys.has(normTrackKey(r?.[logCols.track])));
    }

    if (years.length && logCols.date) {
      out = out.filter((r) => years.includes(safeYearFromDate(r?.[logCols.date])));
    }

    return out;
  }, [logRows, series, excludeExhibition, selectedTrackKeys, years, logCols]);

  const racesForTrack = useMemo(() => {
    const map = new Map();

    for (const r of filteredLogs) {
      const key = buildRaceKey(r, logCols);
      if (!key) continue;

      if (!map.has(key)) {
        map.set(key, {
          race_key: key,
          date: logCols.date ? String(r?.[logCols.date] ?? "") : "",
          race: logCols.race ? String(r?.[logCols.race] ?? "") : "",
          series: logCols.series ? String(r?.[logCols.series] ?? "") : "",
          track: logCols.track ? String(r?.[logCols.track] ?? "") : "",
          rows: [],
        });
      }

      map.get(key).rows.push(r);
    }

    return Array.from(map.values()).map((race) => {
      const winner = race.rows.find((r) => toNum(logCols.finish ? r?.[logCols.finish] : null) === 1);
      const winnerStart = toNum(logCols.start ? winner?.[logCols.start] : null);

      const lapsLedVals = race.rows.map((r) => toNum(logCols.laps_led ? r?.[logCols.laps_led] : null)).filter((v) => v != null);
      const fastLapVals = race.rows.map((r) => toNum(logCols.fastest_lap ? r?.[logCols.fastest_lap] : null)).filter((v) => v != null);
      const finishVals = race.rows.map((r) => toNum(logCols.finish ? r?.[logCols.finish] : null)).filter((v) => v != null);
      const dkVals = race.rows.map((r) => getDkScore(r)).filter((v) => v != null);
      const fdVals = race.rows.map((r) => getFdScore(r)).filter((v) => v != null);

      const llTotal = sum(lapsLedVals);
      const flTotal = sum(fastLapVals);

      const llLeaderShare = llTotal ? Math.max(...lapsLedVals) / llTotal : null;
      const flLeaderShare = flTotal ? Math.max(...fastLapVals) / flTotal : null;

      return {
        ...race,
        drivers: race.rows.length,
        winner_start: winnerStart,
        winner_started_top5: winnerStart != null ? winnerStart <= 5 : null,
        ll_leader_share: llLeaderShare,
        fl_leader_share: flLeaderShare,
        avg_finish: avg(finishVals),
        avg_dk: avg(dkVals),
        median_dk: median(dkVals),
        max_dk: dkVals.length ? Math.max(...dkVals) : null,
        avg_fd: avg(fdVals),
        median_fd: median(fdVals),
        max_fd: fdVals.length ? Math.max(...fdVals) : null,
        front_row_laps_led_share: raceFieldMetricShare(race.rows, logCols.laps_led, logCols.start, 2),
        top5_laps_led_share: raceFieldMetricShare(race.rows, logCols.laps_led, logCols.start, 5),
        top10_laps_led_share: raceFieldMetricShare(race.rows, logCols.laps_led, logCols.start, 10),
        front_row_fast_lap_share: raceFieldMetricShare(race.rows, logCols.fastest_lap, logCols.start, 2),
        top5_fast_lap_share: raceFieldMetricShare(race.rows, logCols.fastest_lap, logCols.start, 5),
        top10_fast_lap_share: raceFieldMetricShare(race.rows, logCols.fastest_lap, logCols.start, 10),
      };
    });
  }, [filteredLogs, logCols]);

  const incidentBenchmarksByTrack = useMemo(() => {
    const trackRaceMap = new Map();

    for (const r of logRows) {
      // Apply the same Series filter.
      if (series !== "All" && logCols.series) {
        if (String(r?.[logCols.series] ?? "") !== series) continue;
      }

      // Apply the same Cup exhibition filter.
      if (excludeExhibition && logCols.series && logCols.race) {
        const s = String(r?.[logCols.series] ?? "");
        const raceName = String(r?.[logCols.race] ?? "");
        if (isExhibitionRace(s, raceName)) continue;
      }

      // Apply the same Year filter.
      if (years.length && logCols.date) {
        const y = safeYearFromDate(r?.[logCols.date]);
        if (!years.includes(y)) continue;
      }

      const trackName = logCols.track ? String(r?.[logCols.track] ?? "").trim() : "";
      if (!trackName) continue;

      const trackKey = normTrackKey(trackName);
      if (!trackKey) continue;

      const raceKey = buildRaceKey(r, logCols);
      if (!raceKey) continue;

      if (!trackRaceMap.has(trackKey)) {
        trackRaceMap.set(trackKey, new Map());
      }

      const raceMap = trackRaceMap.get(trackKey);

      if (!raceMap.has(raceKey)) {
        raceMap.set(raceKey, {
          cautions: null,
          dnf: null,
        });
      }

      const race = raceMap.get(raceKey);

      if (race.cautions == null && logCols.cautions) {
        const v = toNum(r?.[logCols.cautions]);
        if (v != null) race.cautions = v;
      }

      if (race.dnf == null && logCols.dnf) {
        const v = toNum(r?.[logCols.dnf]);
        if (v != null) race.dnf = v;
      }
    }

    const out = new Map();

    for (const [trackKey, raceMap] of trackRaceMap.entries()) {
      const races = Array.from(raceMap.values());
      const cautionVals = races.map((r) => r.cautions).filter((v) => v != null);
      const dnfVals = races.map((r) => r.dnf).filter((v) => v != null);

      out.set(trackKey, {
        avg_cautions: avg(cautionVals),
        avg_dnfs: avg(dnfVals),
        caution_races: cautionVals.length,
        dnf_races: dnfVals.length,
      });
    }

    return out;
  }, [
    logRows,
    logCols,
    series,
    years,
    excludeExhibition,
  ]);

  const raceIncidentSummary = useMemo(() => {
    if (!racesForTrack.length) {
      return {
        avg_cautions: null,
        avg_dnfs: null,
        caution_races: 0,
        dnf_races: 0,
      };
    }

    const cautionsByRace = [];
    const dnfsByRace = [];

    for (const race of racesForTrack) {
      if (logCols.cautions) {
        const cautionValue = race.rows
          .map((r) => toNum(r?.[logCols.cautions]))
          .find((v) => v != null);

        if (cautionValue != null) cautionsByRace.push(cautionValue);
      }

      if (logCols.dnf) {
        const dnfValue = race.rows
          .map((r) => toNum(r?.[logCols.dnf]))
          .find((v) => v != null);

        if (dnfValue != null) dnfsByRace.push(dnfValue);
      }
    }

    return {
      avg_cautions: avg(cautionsByRace),
      avg_dnfs: avg(dnfsByRace),
      caution_races: cautionsByRace.length,
      dnf_races: dnfsByRace.length,
    };
  }, [racesForTrack, logCols.cautions, logCols.dnf]);

  const raceIncidentPercentiles = useMemo(() => {
    const selectedKey = normTrackKey(selectedMeta?.track || track);

    const selected =
      incidentBenchmarksByTrack.get(selectedKey) || raceIncidentSummary;

    const cautionValues = Array.from(incidentBenchmarksByTrack.values())
      .map((x) => x?.avg_cautions)
      .filter((v) => v != null && Number.isFinite(v));

    const dnfValues = Array.from(incidentBenchmarksByTrack.values())
      .map((x) => x?.avg_dnfs)
      .filter((v) => v != null && Number.isFinite(v));

    return {
      cautions:
        selected?.avg_cautions != null
          ? percentileWithin(cautionValues, selected.avg_cautions)
          : null,
      dnfs:
        selected?.avg_dnfs != null
          ? percentileWithin(dnfValues, selected.avg_dnfs)
          : null,
    };
  }, [
    selectedMeta,
    track,
    incidentBenchmarksByTrack,
    raceIncidentSummary,
  ]);

  const startPositionData = useMemo(() => {
    const map = new Map();

    for (const r of filteredLogs) {
      const start = toNum(logCols.start ? r?.[logCols.start] : null);
      if (start == null) continue;

      if (!map.has(start)) {
        map.set(start, {
          start,
          count: 0,
          finishes: [],
          pds: [],
          laps_led: [],
          fast_laps: [],
          dk_scores: [],
          fd_scores: [],
          wins: 0,
          top5: 0,
          top10: 0,
        });
      }

      const row = map.get(start);
      const finish = toNum(logCols.finish ? r?.[logCols.finish] : null);
      const ll = toNum(logCols.laps_led ? r?.[logCols.laps_led] : null);
      const fl = toNum(logCols.fastest_lap ? r?.[logCols.fastest_lap] : null);
      const dk = getDkScore(r);
      const fd = getFdScore(r);

      row.count += 1;
      if (finish != null) row.finishes.push(finish);
      if (finish != null) row.pds.push(start - finish);
      if (ll != null) row.laps_led.push(ll);
      if (fl != null) row.fast_laps.push(fl);
      if (dk != null) row.dk_scores.push(dk);
      if (fd != null) row.fd_scores.push(fd);
      if (finish === 1) row.wins += 1;
      if (finish != null && finish <= 5) row.top5 += 1;
      if (finish != null && finish <= 10) row.top10 += 1;
    }

    return Array.from(map.values())
      .sort((a, b) => a.start - b.start)
      .map((r) => ({
        start: r.start,
        count: r.count,
        avg_finish: avg(r.finishes),
        avg_pd: avg(r.pds),
        avg_laps_led: avg(r.laps_led),
        avg_fast_laps: avg(r.fast_laps),
        avg_dk: avg(r.dk_scores),
        avg_fd: avg(r.fd_scores),
        win_pct: r.count ? r.wins / r.count : null,
        top5_pct: r.count ? r.top5 / r.count : null,
        top10_pct: r.count ? r.top10 / r.count : null,
      }));
  }, [filteredLogs, logCols]);

  const behaviorSummary = useMemo(() => {
    if (!filteredLogs.length) return null;

    const starts = filteredLogs.map((r) => toNum(logCols.start ? r?.[logCols.start] : null));
    const finishes = filteredLogs.map((r) => toNum(logCols.finish ? r?.[logCols.finish] : null));
    const pds = filteredLogs.map((r) => {
      const s = toNum(logCols.start ? r?.[logCols.start] : null);
      const f = toNum(logCols.finish ? r?.[logCols.finish] : null);
      return s != null && f != null ? s - f : null;
    });

    const absPds = pds.map((v) => (v == null ? null : Math.abs(v)));
    const lapsLed = filteredLogs.map((r) => toNum(logCols.laps_led ? r?.[logCols.laps_led] : null));
    const fastLaps = filteredLogs.map((r) => toNum(logCols.fastest_lap ? r?.[logCols.fastest_lap] : null));
    const ratings = filteredLogs.map((r) => toNum(logCols.rating ? r?.[logCols.rating] : null));
    const gfpd = filteredLogs.map((r) => toNum(logCols.green_flag_passing_diff ? r?.[logCols.green_flag_passing_diff] : null));
    const qualityPasses = filteredLogs.map((r) => toNum(logCols.quality_passes ? r?.[logCols.quality_passes] : null));
    const dkScores = filteredLogs.map((r) => getDkScore(r));
    const fdScores = filteredLogs.map((r) => getFdScore(r));

    const winners = filteredLogs.filter((r) => toNum(logCols.finish ? r?.[logCols.finish] : null) === 1);
    const top5StartsByWinner = winners
      .map((r) => toNum(logCols.start ? r?.[logCols.start] : null))
      .filter((v) => v != null && v <= 5);

    const top10Starters = filteredLogs.filter((r) => {
      const s = toNum(logCols.start ? r?.[logCols.start] : null);
      return s != null && s <= 10;
    });

    const top10Carry = top10Starters.length
      ? top10Starters.filter((r) => {
          const f = toNum(logCols.finish ? r?.[logCols.finish] : null);
          return f != null && f <= 10;
        }).length / top10Starters.length
      : null;

    const raceDominatorShares = racesForTrack.map((race) => race.ll_leader_share);
    const raceFastLapShares = racesForTrack.map((race) => race.fl_leader_share);
    const raceFinishStd = racesForTrack.map((race) =>
      stdDev(
        race.rows
          .map((r) => toNum(logCols.finish ? r?.[logCols.finish] : null))
          .filter((v) => v != null)
      )
    );

    const startFinishCorr = corr(starts, finishes);
    const startDkCorr = corr(starts, dkScores);
    const startFdCorr = corr(starts, fdScores);
    const startLapsLedCorr = corr(starts, lapsLed);
    const startFastLapsCorr = corr(starts, fastLaps);
    const finishDkCorr = corr(finishes, dkScores);
    const finishFdCorr = corr(finishes, fdScores);
    const avgAbsPd = avg(absPds);
    const winnerTop5StartPct = winners.length ? top5StartsByWinner.length / winners.length : null;
    const dominatorConcentration = avg(raceDominatorShares);
    const fastLapConcentration = avg(raceFastLapShares);
    const finishVolatility = avg(raceFinishStd);

    const frontRowLapsLedShare = shareOfTotal(filteredLogs, logCols.laps_led, (r) => {
      const s = toNum(logCols.start ? r?.[logCols.start] : null);
      return s != null && s <= 2;
    });
    const top5LapsLedShare = shareOfTotal(filteredLogs, logCols.laps_led, (r) => {
      const s = toNum(logCols.start ? r?.[logCols.start] : null);
      return s != null && s <= 5;
    });
    const top10LapsLedShare = shareOfTotal(filteredLogs, logCols.laps_led, (r) => {
      const s = toNum(logCols.start ? r?.[logCols.start] : null);
      return s != null && s <= 10;
    });

    const frontRowFastLapShare = shareOfTotal(filteredLogs, logCols.fastest_lap, (r) => {
      const s = toNum(logCols.start ? r?.[logCols.start] : null);
      return s != null && s <= 2;
    });
    const top5FastLapShare = shareOfTotal(filteredLogs, logCols.fastest_lap, (r) => {
      const s = toNum(logCols.start ? r?.[logCols.start] : null);
      return s != null && s <= 5;
    });
    const top10FastLapShare = shareOfTotal(filteredLogs, logCols.fastest_lap, (r) => {
      const s = toNum(logCols.start ? r?.[logCols.start] : null);
      return s != null && s <= 10;
    });

    const type = getTrackType(selectedMeta);

    let trackPositionDependency = avg([
      startFinishCorr,
      top10Carry,
      winnerTop5StartPct,
      top5LapsLedShare,
      top5FastLapShare,
    ]);

    if (type === "superspeedway" && trackPositionDependency != null) {
      trackPositionDependency *= 0.72;
    }

    const chaosParts = [];
    let chaosWeight = 0;

    if (avgAbsPd != null) {
      chaosParts.push(Math.min(avgAbsPd / 16, 1) * 0.4);
      chaosWeight += 0.4;
    }
    if (finishVolatility != null) {
      chaosParts.push(Math.min(finishVolatility / 15, 1) * 0.35);
      chaosWeight += 0.35;
    }
    if (startFinishCorr != null) {
      chaosParts.push((1 - Math.max(0, Math.min(startFinishCorr, 1))) * 0.25);
      chaosWeight += 0.25;
    }

    const chaosScore = chaosWeight
      ? chaosParts.reduce((a, b) => a + b, 0) / chaosWeight
      : null;

    const cleanDkScores = dkScores.filter((v) => v != null);
    const cleanFdScores = fdScores.filter((v) => v != null);

    return {
      races_used: racesForTrack.length,
      rows_used: filteredLogs.length,
      start_finish_corr: startFinishCorr,
      start_finish_r2: r2FromCorr(startFinishCorr),
      start_dk_corr: startDkCorr,
      start_dk_r2: r2FromCorr(startDkCorr),
      start_fd_corr: startFdCorr,
      start_fd_r2: r2FromCorr(startFdCorr),
      start_laps_led_corr: startLapsLedCorr,
      start_laps_led_r2: r2FromCorr(startLapsLedCorr),
      start_fast_laps_corr: startFastLapsCorr,
      start_fast_laps_r2: r2FromCorr(startFastLapsCorr),
      finish_dk_corr: finishDkCorr,
      finish_dk_r2: r2FromCorr(finishDkCorr),
      finish_fd_corr: finishFdCorr,
      finish_fd_r2: r2FromCorr(finishFdCorr),
      finish_std: stdDev(finishes),
      pd_std: stdDev(pds),
      abs_pd_std: stdDev(absPds),
      laps_led_std: stdDev(lapsLed),
      fast_laps_std: stdDev(fastLaps),
      median_finish: median(finishes),
      avg_pd: avg(pds),
      avg_abs_pd: avgAbsPd,
      winner_from_top5_start_pct: winnerTop5StartPct,
      top10_start_to_top10_finish_pct: top10Carry,
      dominator_concentration: dominatorConcentration,
      fast_lap_concentration: fastLapConcentration,
      finish_volatility: finishVolatility,
      avg_rating: avg(ratings),
      avg_green_flag_passing_diff: avg(gfpd),
      avg_quality_passes: avg(qualityPasses),
      track_position_dependency: trackPositionDependency,
      chaos_score: chaosScore,
      front_row_laps_led_share: frontRowLapsLedShare,
      top5_laps_led_share: top5LapsLedShare,
      top10_laps_led_share: top10LapsLedShare,
      front_row_fast_lap_share: frontRowFastLapShare,
      top5_fast_lap_share: top5FastLapShare,
      top10_fast_lap_share: top10FastLapShare,
      total_laps_led: sum(lapsLed),
      total_fast_laps: sum(fastLaps),
      avg_dk: avg(cleanDkScores),
      median_dk: median(cleanDkScores),
      dk_std: stdDev(cleanDkScores),
      max_dk: cleanDkScores.length ? Math.max(...cleanDkScores) : null,
      avg_fd: avg(cleanFdScores),
      median_fd: median(cleanFdScores),
      fd_std: stdDev(cleanFdScores),
      max_fd: cleanFdScores.length ? Math.max(...cleanFdScores) : null,
      type,
    };
  }, [filteredLogs, racesForTrack, logCols, selectedMeta]);

  const startBuckets = useMemo(() => {
    const buckets = new Map();

    for (const r of filteredLogs) {
      const bucket = bucketStart(logCols.start ? r?.[logCols.start] : null);

      if (!buckets.has(bucket)) {
        buckets.set(bucket, {
          bucket,
          count: 0,
          finishes: [],
          pds: [],
          wins: 0,
          top5: 0,
          top10: 0,
          laps_led: [],
          fast_laps: [],
          dk_scores: [],
          fd_scores: [],
        });
      }

      const row = buckets.get(bucket);
      const s = toNum(logCols.start ? r?.[logCols.start] : null);
      const f = toNum(logCols.finish ? r?.[logCols.finish] : null);
      const ll = toNum(logCols.laps_led ? r?.[logCols.laps_led] : null);
      const fl = toNum(logCols.fastest_lap ? r?.[logCols.fastest_lap] : null);
      const dk = getDkScore(r);
      const fd = getFdScore(r);

      row.count += 1;
      if (f != null) row.finishes.push(f);
      if (s != null && f != null) row.pds.push(s - f);
      if (ll != null) row.laps_led.push(ll);
      if (fl != null) row.fast_laps.push(fl);
      if (dk != null) row.dk_scores.push(dk);
      if (fd != null) row.fd_scores.push(fd);
      if (f === 1) row.wins += 1;
      if (f != null && f <= 5) row.top5 += 1;
      if (f != null && f <= 10) row.top10 += 1;
    }

    const order = ["1-2", "3-5", "6-10", "11-15", "16-20", "21-25", "26+", "Unknown"];

    const totalLL = sum(filteredLogs.map((r) => toNum(logCols.laps_led ? r?.[logCols.laps_led] : null)));
    const totalFL = sum(filteredLogs.map((r) => toNum(logCols.fastest_lap ? r?.[logCols.fastest_lap] : null)));

    return order
      .filter((k) => buckets.has(k))
      .map((k, idx) => {
        const b = buckets.get(k);
        const llSum = sum(b.laps_led) || 0;
        const flSum = sum(b.fast_laps) || 0;

        return {
          bucket: b.bucket,
          sort_order: idx + 1,
          count: b.count,
          avg_finish: avg(b.finishes),
          median_finish: median(b.finishes),
          avg_pd: avg(b.pds),
          win_pct: b.count ? b.wins / b.count : null,
          top5_pct: b.count ? b.top5 / b.count : null,
          top10_pct: b.count ? b.top10 / b.count : null,
          laps_led_share: totalLL ? llSum / totalLL : null,
          fast_lap_share: totalFL ? flSum / totalFL : null,
          avg_dk: avg(b.dk_scores),
          median_dk: median(b.dk_scores),
          max_dk: b.dk_scores.length ? Math.max(...b.dk_scores) : null,
          avg_fd: avg(b.fd_scores),
          median_fd: median(b.fd_scores),
          max_fd: b.fd_scores.length ? Math.max(...b.fd_scores) : null,
        };
      });
  }, [filteredLogs, logCols]);

  const behaviorByTrack = useMemo(() => {
    const trackMap = new Map();

    for (const meta of metaRows) {
      const metaKeys = [meta.track, ...(meta.aliases || [])].map(normTrackKey).filter(Boolean);

      const matched = logRows.filter((r) => {
        if (series !== "All" && logCols.series) {
          if (String(r?.[logCols.series] ?? "") !== series) return false;
        }

        if (excludeExhibition && logCols.series && logCols.race) {
          const s = String(r?.[logCols.series] ?? "");
          const raceName = String(r?.[logCols.race] ?? "");
          if (isExhibitionRace(s, raceName)) return false;
        }

        if (years.length && logCols.date) {
          const y = safeYearFromDate(r?.[logCols.date]);
          if (!years.includes(y)) return false;
        }

        return metaKeys.includes(normTrackKey(r?.[logCols.track]));
      });

      if (!matched.length) {
        trackMap.set(meta.track, null);
        continue;
      }

      const starts = matched.map((r) => toNum(logCols.start ? r?.[logCols.start] : null));
      const finishes = matched.map((r) => toNum(logCols.finish ? r?.[logCols.finish] : null));
      const pds = matched.map((r) => {
        const s = toNum(logCols.start ? r?.[logCols.start] : null);
        const f = toNum(logCols.finish ? r?.[logCols.finish] : null);
        return s != null && f != null ? s - f : null;
      });

      const lapsLed = matched.map((r) => toNum(logCols.laps_led ? r?.[logCols.laps_led] : null));
      const fastLaps = matched.map((r) => toNum(logCols.fastest_lap ? r?.[logCols.fastest_lap] : null));
      const dkScores = matched.map((r) => getDkScore(r));
      const fdScores = matched.map((r) => getFdScore(r));
      const ratings = matched.map((r) => toNum(logCols.rating ? r?.[logCols.rating] : null));

      const winners = matched.filter((r) => toNum(logCols.finish ? r?.[logCols.finish] : null) === 1);
      const top5WinnerStarts = winners
        .map((r) => toNum(logCols.start ? r?.[logCols.start] : null))
        .filter((v) => v != null && v <= 5);

      const top10Starts = matched.filter((r) => {
        const s = toNum(logCols.start ? r?.[logCols.start] : null);
        return s != null && s <= 10;
      });

      const top10Carry = top10Starts.length
        ? top10Starts.filter((r) => {
            const f = toNum(logCols.finish ? r?.[logCols.finish] : null);
            return f != null && f <= 10;
          }).length / top10Starts.length
        : null;

      const raceMap = new Map();
      for (const r of matched) {
        const rk = buildRaceKey(r, logCols);
        if (!raceMap.has(rk)) raceMap.set(rk, []);
        raceMap.get(rk).push(r);
      }

      const raceRows = Array.from(raceMap.values());

      const raceDominatorShares = raceRows.map((rows) => {
        const vals = rows.map((r) => toNum(logCols.laps_led ? r?.[logCols.laps_led] : null)).filter((v) => v != null);
        const total = sum(vals);
        return vals.length && total ? Math.max(...vals) / total : null;
      });

      const raceFastShares = raceRows.map((rows) => {
        const vals = rows.map((r) => toNum(logCols.fastest_lap ? r?.[logCols.fastest_lap] : null)).filter((v) => v != null);
        const total = sum(vals);
        return vals.length && total ? Math.max(...vals) / total : null;
      });

      const raceFinishStd = raceRows.map((rows) =>
        stdDev(rows.map((r) => toNum(logCols.finish ? r?.[logCols.finish] : null)).filter((v) => v != null))
      );

      const frontRowLL = shareOfTotal(matched, logCols.laps_led, (r) => {
        const s = toNum(logCols.start ? r?.[logCols.start] : null);
        return s != null && s <= 2;
      });
      const frontRowFL = shareOfTotal(matched, logCols.fastest_lap, (r) => {
        const s = toNum(logCols.start ? r?.[logCols.start] : null);
        return s != null && s <= 2;
      });
      const top5LL = shareOfTotal(matched, logCols.laps_led, (r) => {
        const s = toNum(logCols.start ? r?.[logCols.start] : null);
        return s != null && s <= 5;
      });
      const top5FL = shareOfTotal(matched, logCols.fastest_lap, (r) => {
        const s = toNum(logCols.start ? r?.[logCols.start] : null);
        return s != null && s <= 5;
      });

      let tpd = avg([
        corr(starts, finishes),
        top10Carry,
        winners.length ? top5WinnerStarts.length / winners.length : null,
        top5LL,
        top5FL,
      ]);

      if (getTrackType(meta) === "superspeedway" && tpd != null) {
        tpd *= 0.72;
      }

      const absPds = pds.map((v) => (v == null ? null : Math.abs(v)));
      const absPdAvg = avg(absPds);
      const sfCorr = corr(starts, finishes);
      const startDkCorr = corr(starts, dkScores);
      const startFdCorr = corr(starts, fdScores);
      const startLapsLedCorr = corr(starts, lapsLed);
      const startFastLapsCorr = corr(starts, fastLaps);
      const finishDkCorr = corr(finishes, dkScores);
      const finishFdCorr = corr(finishes, fdScores);

      const finishVolatility = avg(raceFinishStd);

      const chaosParts = [];
      let chaosWeight = 0;

      if (absPdAvg != null) {
        chaosParts.push(Math.min(absPdAvg / 16, 1) * 0.4);
        chaosWeight += 0.4;
      }

      if (finishVolatility != null) {
        chaosParts.push(Math.min(finishVolatility / 15, 1) * 0.35);
        chaosWeight += 0.35;
      }

      if (sfCorr != null) {
        chaosParts.push((1 - Math.max(0, Math.min(sfCorr, 1))) * 0.25);
        chaosWeight += 0.25;
      }

      const chaos = chaosWeight
        ? chaosParts.reduce((a, b) => a + b, 0) / chaosWeight
        : null;

      trackMap.set(meta.track, {
        races_used: raceMap.size,
        rows_used: matched.length,
        start_finish_corr: sfCorr,
        start_finish_r2: r2FromCorr(sfCorr),
        start_dk_corr: startDkCorr,
        start_dk_r2: r2FromCorr(startDkCorr),
        start_fd_corr: startFdCorr,
        start_fd_r2: r2FromCorr(startFdCorr),
        start_laps_led_corr: startLapsLedCorr,
        start_laps_led_r2: r2FromCorr(startLapsLedCorr),
        start_fast_laps_corr: startFastLapsCorr,
        start_fast_laps_r2: r2FromCorr(startFastLapsCorr),
        finish_dk_corr: finishDkCorr,
        finish_dk_r2: r2FromCorr(finishDkCorr),
        finish_fd_corr: finishFdCorr,
        finish_fd_r2: r2FromCorr(finishFdCorr),
        finish_std: stdDev(finishes),
        pd_std: stdDev(pds),
        abs_pd_std: stdDev(absPds),
        laps_led_std: stdDev(lapsLed),
        fast_laps_std: stdDev(fastLaps),
        dk_std: stdDev(dkScores),
        fd_std: stdDev(fdScores),
        avg_dk: avg(dkScores),
        avg_fd: avg(fdScores),
        avg_rating: avg(ratings),
        avg_abs_pd: absPdAvg,
        winner_from_top5_start_pct: winners.length ? top5WinnerStarts.length / winners.length : null,
        top10_start_to_top10_finish_pct: top10Carry,
        dominator_concentration: avg(raceDominatorShares),
        fast_lap_concentration: avg(raceFastShares),
        finish_volatility: avg(raceFinishStd),
        track_position_dependency: tpd,
        chaos_score: chaos,
        front_row_laps_led_share: frontRowLL,
        front_row_fast_lap_share: frontRowFL,
      });
    }

    return trackMap;
  }, [metaRows, logRows, logCols, series, years, excludeExhibition]);

  const similarTracks = useMemo(() => {
    if (!selectedMeta) return [];

    const baseBehavior = behaviorByTrack.get(selectedMeta.track) || null;

    const rows = metaRows
      .filter((m) => normTrackKey(m.track) !== normTrackKey(selectedMeta.track))
      .map((m) => {
        const compBehavior = behaviorByTrack.get(m.track) || null;
        const sim = scoreTrackSimilarity(selectedMeta, m, baseBehavior, compBehavior, similarityMode);

        return {
          track: m.track,
          track_type: getTrackType(m),
          custom_groups: (m.custom_groups || []).join(", "),
          surface: m.surface || "—",
          shape: m.shape || "—",
          size_group: m.size_group || "—",
          length_miles: toNum(m.length_miles),
          banking_avg: getBanking(m).avg,
          similarity: sim,
          races_used: compBehavior?.races_used ?? null,
          start_finish_corr: compBehavior?.start_finish_corr ?? null,
          avg_abs_pd: compBehavior?.avg_abs_pd ?? null,
          track_position_dependency: compBehavior?.track_position_dependency ?? null,
          chaos_score: compBehavior?.chaos_score ?? null,
        };
      })
      .filter((x) => x.similarity != null);

    return sortRows(rows, similarSort.key, similarSort.dir).slice(0, 15);
  }, [selectedMeta, metaRows, behaviorByTrack, similarityMode, similarSort]);

  const sortedStartBuckets = useMemo(() => sortRows(startBuckets, bucketSort.key, bucketSort.dir), [startBuckets, bucketSort]);
  const sortedRacesForTrack = useMemo(() => sortRows(racesForTrack, raceSort.key, raceSort.dir), [racesForTrack, raceSort]);

  const banking = useMemo(() => getBanking(selectedMeta), [selectedMeta]);

  const quickRead = useMemo(() => {
    const comparisonBehaviors = Array.from(behaviorByTrack.values()).filter(
      (b) => b && b.races_used
    );

    return getTrackFlavor(
      selectedMeta,
      behaviorSummary,
      comparisonBehaviors
    );
  }, [selectedMeta, behaviorSummary, behaviorByTrack]);

  const scatterMetricOptions = useMemo(() => {
    const base = [
      { value: "start", label: "Start" },
      { value: "finish", label: "Finish" },
      { value: "pd", label: "PD" },
      { value: "laps_led", label: "Laps Led" },
      { value: "fast_laps", label: "Fast Laps" },
      { value: "rating", label: "Rating" },
      { value: "avg_run", label: "Avg Run Pos" },
      { value: "quality_passes", label: "Quality Passes" },
      { value: "gfpd", label: "Green Flag Passing Diff" },
    ];
    if (hasDk) base.splice(3, 0, { value: "dk", label: "DK Score" });
    if (hasFd) base.splice(4, 0, { value: "fd", label: "FD Score" });
    return base;
  }, [hasDk, hasFd]);

  const trendMetricOptions = useMemo(() => {
    const base = [
      { value: "winner_start", label: "Winner Start" },
      { value: "ll_leader_share", label: "LL Leader Share" },
      { value: "fl_leader_share", label: "FL Leader Share" },
      { value: "front_row_laps_led_share", label: "Front Row LL Share" },
      { value: "front_row_fast_lap_share", label: "Front Row FL Share" },
    ];
    if (hasDk) base.unshift({ value: "avg_dk", label: "Avg DK" });
    if (hasFd) base.splice(hasDk ? 1 : 0, 0, { value: "avg_fd", label: "Avg FD" });
    return base;
  }, [hasDk, hasFd]);

  const bucketMetricOptions = useMemo(() => {
    const base = [
      { value: "avg_finish", label: "Avg Finish" },
      { value: "avg_pd", label: "Avg PD" },
      { value: "win_pct", label: "Win %" },
      { value: "top5_pct", label: "Top 5 %" },
      { value: "top10_pct", label: "Top 10 %" },
      { value: "laps_led_share", label: "LL Share" },
      { value: "fast_lap_share", label: "FL Share" },
    ];
    if (hasDk) base.splice(5, 0, { value: "avg_dk", label: "Avg DK" });
    if (hasFd) base.splice(hasDk ? 6 : 5, 0, { value: "avg_fd", label: "Avg FD" });
    return base;
  }, [hasDk, hasFd]);

    const startPosMetricOptions = useMemo(() => {
    const base = [
      { value: "avg_finish", label: "Avg Finish" },
      { value: "avg_pd", label: "Avg PD" },
      { value: "avg_laps_led", label: "Avg Laps Led" },
      { value: "avg_fast_laps", label: "Avg Fast Laps" },
      { value: "win_pct", label: "Win %" },
      { value: "top5_pct", label: "Top 5 %" },
      { value: "top10_pct", label: "Top 10 %" },
    ];

    if (hasDk) base.unshift({ value: "avg_dk", label: "Avg DK Score" });
    if (hasFd) base.splice(hasDk ? 1 : 0, 0, { value: "avg_fd", label: "Avg FD Score" });

    return base;
  }, [hasDk, hasFd]);

  const startPosMetricIsPct = ["win_pct", "top5_pct", "top10_pct"].includes(startPosMetric);

  const scatterPoints = useMemo(() => {
    const getMetric = (r, key) => {
      if (key === "start") return toNum(logCols.start ? r?.[logCols.start] : null);
      if (key === "finish") return toNum(logCols.finish ? r?.[logCols.finish] : null);
      if (key === "pd") {
        const s = toNum(logCols.start ? r?.[logCols.start] : null);
        const f = toNum(logCols.finish ? r?.[logCols.finish] : null);
        return s != null && f != null ? s - f : null;
      }
      if (key === "dk") return getDkScore(r);
      if (key === "fd") return getFdScore(r);
      if (key === "laps_led") return toNum(logCols.laps_led ? r?.[logCols.laps_led] : null);
      if (key === "fast_laps") return toNum(logCols.fastest_lap ? r?.[logCols.fastest_lap] : null);
      if (key === "rating") return toNum(logCols.rating ? r?.[logCols.rating] : null);
      if (key === "avg_run") return toNum(logCols.avg_run ? r?.[logCols.avg_run] : null);
      if (key === "quality_passes") return toNum(logCols.quality_passes ? r?.[logCols.quality_passes] : null);
      if (key === "gfpd") return toNum(logCols.green_flag_passing_diff ? r?.[logCols.green_flag_passing_diff] : null);
      return null;
    };

    return filteredLogs
      .map((r, i) => {
        const x = getMetric(r, scatterX);
        const y = getMetric(r, scatterY);
        return {
          id: i,
          driver: logCols.driver ? String(r?.[logCols.driver] ?? "—") : "—",
          date: logCols.date ? String(r?.[logCols.date] ?? "") : "",
          race: logCols.race ? String(r?.[logCols.race] ?? "") : "",
          startBucket: bucketStart(logCols.start ? r?.[logCols.start] : null),
          x,
          y,
        };
      })
      .filter((p) => p.x != null && p.y != null);
  }, [filteredLogs, logCols, scatterX, scatterY]);

  const scatterCorr = useMemo(() => corr(scatterPoints.map((p) => p.x), scatterPoints.map((p) => p.y)), [scatterPoints]);

  const scatterRegressionLine = useMemo(() => {
    if (scatterPoints.length < 2) return [];
    const xs = scatterPoints.map((p) => p.x);
    const ys = scatterPoints.map((p) => p.y);
    const reg = linearRegression(xs, ys);
    if (!reg) return [];
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    return [
      { x: minX, y: reg.slope * minX + reg.intercept },
      { x: maxX, y: reg.slope * maxX + reg.intercept },
    ];
  }, [scatterPoints]);

  const scatterMeans = useMemo(() => {
    return {
      x: avg(scatterPoints.map((p) => p.x)),
      y: avg(scatterPoints.map((p) => p.y)),
    };
  }, [scatterPoints]);

  const histogramData = useMemo(() => {
    const vals = filteredLogs.map((r) => {
      if (histMetric === "dk") return getDkScore(r);
      if (histMetric === "fd") return getFdScore(r);
      if (histMetric === "finish") return toNum(logCols.finish ? r?.[logCols.finish] : null);
      if (histMetric === "pd") {
        const s = toNum(logCols.start ? r?.[logCols.start] : null);
        const f = toNum(logCols.finish ? r?.[logCols.finish] : null);
        return s != null && f != null ? s - f : null;
      }
      if (histMetric === "laps_led") return toNum(logCols.laps_led ? r?.[logCols.laps_led] : null);
      if (histMetric === "fast_laps") return toNum(logCols.fastest_lap ? r?.[logCols.fastest_lap] : null);
      return null;
    });
    return buildHistogram(vals, Math.max(4, Math.min(30, Number(histBins) || 12)));
  }, [filteredLogs, histMetric, histBins, logCols]);

  const histogramMeanMedian = useMemo(() => {
    const vals = filteredLogs
      .map((r) => {
        if (histMetric === "dk") return getDkScore(r);
        if (histMetric === "fd") return getFdScore(r);
        if (histMetric === "finish") return toNum(logCols.finish ? r?.[logCols.finish] : null);
        if (histMetric === "pd") {
          const s = toNum(logCols.start ? r?.[logCols.start] : null);
          const f = toNum(logCols.finish ? r?.[logCols.finish] : null);
          return s != null && f != null ? s - f : null;
        }
        if (histMetric === "laps_led") return toNum(logCols.laps_led ? r?.[logCols.laps_led] : null);
        if (histMetric === "fast_laps") return toNum(logCols.fastest_lap ? r?.[logCols.fastest_lap] : null);
        return null;
      })
      .filter((v) => v != null);
    return { mean: avg(vals), median: median(vals) };
  }, [filteredLogs, histMetric, logCols]);

  const trendData = useMemo(() => {
    return [...racesForTrack]
      .sort((a, b) => String(a.date).localeCompare(String(b.date)))
      .map((r) => ({
        date: r.date,
        label: `${r.date}${r.race ? ` • ${r.race}` : ""}`,
        avg_dk: r.avg_dk,
        avg_fd: r.avg_fd,
        winner_start: r.winner_start,
        ll_leader_share: r.ll_leader_share,
        fl_leader_share: r.fl_leader_share,
        front_row_laps_led_share: r.front_row_laps_led_share,
        front_row_fast_lap_share: r.front_row_fast_lap_share,
      }));
  }, [racesForTrack]);

  const trendAvg = useMemo(() => avg(trendData.map((d) => d[trendMetric]).filter((v) => v != null)), [trendData, trendMetric]);

  const bucketChartData = useMemo(() => {
    return startBuckets.map((b) => ({
      bucket: b.bucket,
      value: b[bucketMetric],
    }));
  }, [startBuckets, bucketMetric]);

  const bucketMetricIsPct = ["win_pct", "top5_pct", "top10_pct", "laps_led_share", "fast_lap_share"].includes(bucketMetric);
  const trendMetricIsPct = ["ll_leader_share", "fl_leader_share", "front_row_laps_led_share", "front_row_fast_lap_share"].includes(trendMetric);

  const rankingMetricOptions = useMemo(() => {
    const base = [
      { value: "finish_std", label: "Finish Std Dev", format: "number", higherGood: false },
      { value: "pd_std", label: "PD Std Dev", format: "number", higherGood: false },
      { value: "laps_led_std", label: "Laps Led Std Dev", format: "number", higherGood: false },
      { value: "fast_laps_std", label: "Fast Laps Std Dev", format: "number", higherGood: false },
      { value: "start_finish_corr", label: "Start → Finish Corr", format: "corr", higherGood: true },
      { value: "start_finish_r2", label: "Start → Finish R²", format: "pct", higherGood: true },
      { value: "track_position_dependency", label: "Track Position Dependency", format: "pct", higherGood: true },
      { value: "chaos_score", label: "Chaos Score", format: "pct", higherGood: false },
      { value: "dominator_concentration", label: "Dominator Concentration", format: "pct", higherGood: true },
      { value: "front_row_laps_led_share", label: "Front Row LL Share", format: "pct", higherGood: true },
    ];

    if (hasDk) {
      base.splice(4, 0, { value: "dk_std", label: "DK Score Std Dev", format: "number", higherGood: false });
      base.push({ value: "start_dk_r2", label: "Start → DK R²", format: "pct", higherGood: true });
    }
    if (hasFd) {
      base.splice(hasDk ? 5 : 4, 0, { value: "fd_std", label: "FD Score Std Dev", format: "number", higherGood: false });
      base.push({ value: "start_fd_r2", label: "Start → FD R²", format: "pct", higherGood: true });
    }

    return base;
  }, [hasDk, hasFd]);

  const formatRankingValue = (value, metric = rankingMetric) => {
    const opt = rankingMetricOptions.find((o) => o.value === metric);
    if (!opt) return fmt(value, 3);
    if (opt.format === "pct") return fmtPct(value, 1);
    if (opt.format === "corr") return fmt(value, 3);
    return fmt(value, 2);
  };

  const trackAnalyticsRows = useMemo(() => {
    return metaRows
      .map((m) => {
        const b = behaviorByTrack.get(m.track);
        if (!b || !b.races_used) return null;
        return {
          track: m.track,
          track_type: getTrackType(m),
          races_used: b.races_used,
          rows_used: b.rows_used,
          start_finish_corr: b.start_finish_corr,
          start_finish_r2: b.start_finish_r2,
          start_dk_r2: b.start_dk_r2,
          start_fd_r2: b.start_fd_r2,
          finish_std: b.finish_std,
          pd_std: b.pd_std,
          laps_led_std: b.laps_led_std,
          fast_laps_std: b.fast_laps_std,
          dk_std: b.dk_std,
          fd_std: b.fd_std,
          track_position_dependency: b.track_position_dependency,
          chaos_score: b.chaos_score,
          dominator_concentration: b.dominator_concentration,
          front_row_laps_led_share: b.front_row_laps_led_share,
        };
      })
      .filter(Boolean);
  }, [metaRows, behaviorByTrack]);

  const sortedTrackAnalyticsRows = useMemo(() => {
    const dir = rankingOrder === "asc" ? 1 : -1;
    return [...trackAnalyticsRows]
      .filter((r) => r[rankingMetric] != null && Number.isFinite(r[rankingMetric]))
      .sort((a, b) => (a[rankingMetric] - b[rankingMetric]) * dir)
      .slice(0, 10);
  }, [trackAnalyticsRows, rankingMetric, rankingOrder]);

  const rankingMetricRange = useMemo(() => {
    const vals = sortedTrackAnalyticsRows
      .map((r) => r[rankingMetric])
      .filter((v) => v != null && Number.isFinite(v));
    if (!vals.length) return { min: 0, max: 1, span: 1 };
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    return { min, max, span: max - min || 1 };
  }, [sortedTrackAnalyticsRows, rankingMetric]);

  const topFinishStdTracks = useMemo(() => [...trackAnalyticsRows].filter((r) => r.finish_std != null).sort((a, b) => b.finish_std - a.finish_std).slice(0, 10), [trackAnalyticsRows]);
  const lowStartFinishR2Tracks = useMemo(() => [...trackAnalyticsRows].filter((r) => r.start_finish_r2 != null).sort((a, b) => a.start_finish_r2 - b.start_finish_r2).slice(0, 10), [trackAnalyticsRows]);
  const highStartFinishR2Tracks = useMemo(() => [...trackAnalyticsRows].filter((r) => r.start_finish_r2 != null).sort((a, b) => b.start_finish_r2 - a.start_finish_r2).slice(0, 10), [trackAnalyticsRows]);
  const topDominatorStdTracks = useMemo(() => [...trackAnalyticsRows].filter((r) => r.laps_led_std != null).sort((a, b) => b.laps_led_std - a.laps_led_std).slice(0, 10), [trackAnalyticsRows]);

  const rankingListOptions = useMemo(() => [
    {
      key: "finish_std",
      label: "Highest Finish Std Dev",
      right: "Most volatile finishing results",
      rows: topFinishStdTracks,
      valueLabel: "Std",
      valueKey: "finish_std",
      valueFormat: (v) => fmt(v, 2),
      secondLabel: "Races",
      secondKey: "races_used",
      secondFormat: (v) => fmt(v, 0),
    },
    {
      key: "low_start_finish_r2",
      label: "Lowest Start→Finish R²",
      right: "Most chaos / least start-dependent",
      rows: lowStartFinishR2Tracks,
      valueLabel: "R²",
      valueKey: "start_finish_r2",
      valueFormat: (v) => fmtPct(v, 1),
      secondLabel: "Corr",
      secondKey: "start_finish_corr",
      secondFormat: (v) => fmt(v, 3),
    },
    {
      key: "high_start_finish_r2",
      label: "Highest Start→Finish R²",
      right: "Most track-position dependent",
      rows: highStartFinishR2Tracks,
      valueLabel: "R²",
      valueKey: "start_finish_r2",
      valueFormat: (v) => fmtPct(v, 1),
      secondLabel: "Corr",
      secondKey: "start_finish_corr",
      secondFormat: (v) => fmt(v, 3),
    },
    {
      key: "laps_led_std",
      label: "Highest Laps Led Std Dev",
      right: "Dominator volatility",
      rows: topDominatorStdTracks,
      valueLabel: "Std",
      valueKey: "laps_led_std",
      valueFormat: (v) => fmt(v, 2),
      secondLabel: "Races",
      secondKey: "races_used",
      secondFormat: (v) => fmt(v, 0),
    },
  ], [topFinishStdTracks, lowStartFinishR2Tracks, highStartFinishR2Tracks, topDominatorStdTracks]);

  const activeRankingList = useMemo(() => {
    return rankingListOptions.find((x) => x.key === rankingListMode) || rankingListOptions[0];
  }, [rankingListOptions, rankingListMode]);

  const selectedCorrelationRows = useMemo(() => {
    if (!behaviorSummary) return [];
    return [
      { label: "Start → Finish", corr: behaviorSummary.start_finish_corr, r2: behaviorSummary.start_finish_r2 },
      ...(hasDk ? [{ label: "Start → DK", corr: behaviorSummary.start_dk_corr, r2: behaviorSummary.start_dk_r2 }] : []),
      ...(hasFd ? [{ label: "Start → FD", corr: behaviorSummary.start_fd_corr, r2: behaviorSummary.start_fd_r2 }] : []),
      { label: "Start → Laps Led", corr: behaviorSummary.start_laps_led_corr, r2: behaviorSummary.start_laps_led_r2 },
      { label: "Start → Fast Laps", corr: behaviorSummary.start_fast_laps_corr, r2: behaviorSummary.start_fast_laps_r2 },
      ...(hasDk ? [{ label: "Finish → DK", corr: behaviorSummary.finish_dk_corr, r2: behaviorSummary.finish_dk_r2 }] : []),
      ...(hasFd ? [{ label: "Finish → FD", corr: behaviorSummary.finish_fd_corr, r2: behaviorSummary.finish_fd_r2 }] : []),
    ].filter((r) => r.corr != null || r.r2 != null);
  }, [behaviorSummary, hasDk, hasFd]);


  useEffect(() => {
    if (!hasDk && scatterY === "dk") setScatterY(hasFd ? "fd" : "finish");
    if (!hasFd && scatterY === "fd") setScatterY(hasDk ? "dk" : "finish");
    if (!hasDk && histMetric === "dk") setHistMetric(hasFd ? "fd" : "finish");
    if (!hasFd && histMetric === "fd") setHistMetric(hasDk ? "dk" : "finish");
    if (!hasDk && bucketMetric === "avg_dk") setBucketMetric(hasFd ? "avg_fd" : "avg_finish");
    if (!hasFd && bucketMetric === "avg_fd") setBucketMetric(hasDk ? "avg_dk" : "avg_finish");
    if (!hasDk && trendMetric === "avg_dk") setTrendMetric(hasFd ? "avg_fd" : "winner_start");
    if (!hasFd && trendMetric === "avg_fd") setTrendMetric(hasDk ? "avg_dk" : "winner_start");
  }, [hasDk, hasFd, scatterY, histMetric, bucketMetric, trendMetric]);

  function handleSort(setter, key, defaultDir = "desc") {
    setter((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: defaultDir };
    });
  }

  function resetAll() {
    setSeries("All");
    setYears([]);
    setExcludeExhibition(true);
    setSimilarityMode("blended");
    setTrackSearch("");
    setExpandedRaceKey("");
    setMainView("tables");
    setScatterX("start");
    setScatterY(hasDk ? "dk" : hasFd ? "fd" : "finish");
    setHistMetric(hasDk ? "dk" : hasFd ? "fd" : "finish");
    setHistBins(12);
    setBucketMetric(hasDk ? "avg_dk" : hasFd ? "avg_fd" : "avg_finish");
    setTrendMetric(hasDk ? "avg_dk" : hasFd ? "avg_fd" : "winner_start");
    setStartPosMetric(hasDk ? "avg_dk" : hasFd ? "avg_fd" : "avg_finish");
    setRankingMetric("finish_std");
    setRankingOrder("desc");
    setAnalyticsChartMode("ranking");
    setRankingListMode("finish_std");
    if (metaRows.length) setTrack(metaRows[0].track || "");
  }

  return (
    <div className="min-h-screen bg-slate-950 px-4 md:px-6 py-5 text-slate-100">
      <div className="mx-auto w-full min-w-0">
        <div className="space-y-4">
          <div className="mb-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h1 className="text-xl font-bold tracking-tight text-slate-100">NASCAR Track Data</h1>
                <div className="text-xs font-normal italic text-slate-100">
                  {updatedAt ? `Updated: ${new Date(updatedAt).toLocaleString()}` : ""}
                </div>
              </div>
              <div className="ml-auto flex items-center gap-3">              <div className="ml-auto flex shrink-0 items-center">
                <button
                  onClick={resetAll}
                  className="h-8 w-full min-w-0 rounded-md border border-slate-700 bg-slate-900 px-3 text-xs font-medium text-slate-100 hover:bg-slate-800"
                >
                  Reset
                </button>
              </div>
                <div className="inline-flex items-center gap-1 rounded-2xl border border-slate-800 bg-slate-900/95 px-2">
                  <button
                    type="button"
                    onClick={() => setMainView("tables")}
                    className={`border-b-2 px-3 py-2.5 text-xs font-semibold transition ${mainView === "tables" ? "border-sky-400 text-white" : "border-transparent text-slate-400 hover:text-white"}`}
                  >
                    Tables
                  </button>
                  <button
                    type="button"
                    onClick={() => setMainView("charts")}
                    className={`border-b-2 px-3 py-2.5 text-xs font-semibold transition ${mainView === "charts" ? "border-sky-400 text-white" : "border-transparent text-slate-400 hover:text-white"}`}
                  >
                    Charts
                  </button>
                </div>

              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900/95 p-4 shadow-sm">
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-[2fr_1fr_1fr_1fr]">
              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Track</div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-[180px_1fr]">
                  <input
                    type="text"
                    value={trackSearch}
                    onChange={(e) => setTrackSearch(e.target.value)}
                    placeholder="Search track"
                    className="h-8 min-w-0 rounded-md border border-slate-700 bg-slate-900 px-2 text-xs text-slate-100 outline-none placeholder:text-slate-500 focus:border-slate-500"
                  />
                  <select
                    value={track}
                    onChange={(e) => {
                      setTrack(e.target.value);
                      setExpandedRaceKey("");
                    }}
                    className="h-8 min-w-0 rounded-md border border-slate-700 bg-slate-900 px-2 text-xs text-slate-100 outline-none focus:border-slate-500"
                  >
                    {filteredTrackOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Series</div>
                <select
                  value={series}
                  onChange={(e) => setSeries(e.target.value)}
                  className="h-8 w-full min-w-0 rounded-md border border-slate-700 bg-slate-900 px-2 text-xs text-slate-100 outline-none focus:border-slate-500"
                >
                  {seriesOptions.map((s) => (
                    <option key={s} value={s}>
                      {publicSeriesLabel(s)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Similarity Mode</div>
                <select
                  value={similarityMode}
                  onChange={(e) => setSimilarityMode(e.target.value)}
                  className="h-8 w-full min-w-0 rounded-md border border-slate-700 bg-slate-900 px-2 text-xs text-slate-100 outline-none focus:border-slate-500"
                >
                  <option value="blended">Blended</option>
                  <option value="metadata">Metadata Only</option>
                  <option value="behavior">Behavior Only</option>
                </select>
              </div>

              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Race Filter</div>
                <label className="flex h-8 items-center rounded-md gap-2 border border-slate-700 bg-slate-900 px-2 text-sm text-slate-100">
                  <input
                    type="checkbox"
                    checked={excludeExhibition}
                    onChange={(e) => setExcludeExhibition(e.target.checked)}
                  />
                  <span>Exclude exhibition</span>
                </label>
              </div>


            </div>

            <div className="mt-3">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Years</div>
              <div className="flex flex-wrap gap-1.5">
                {yearOptions.map((y) => {
                  const active = years.includes(y);
                  return (
                    <button
                      key={y}
                      type="button"
                      onClick={() =>
                        setYears((prev) => (prev.includes(y) ? prev.filter((x) => x !== y) : [...prev, y]))
                      }
                      className={`min-w-[54px] rounded-md border px-2 py-1.5 text-xs ${
                        active
                          ? "border-sky-500 bg-sky-900/30 text-sky-200"
                          : "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800"
                      }`}
                    >
                      {y}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {err ? <div className="border-b border-slate-700 px-4 py-2 text-sm text-rose-400">{err}</div> : null}

          {loading ? (
            <div className="px-4 py-6 text-sm text-slate-300">Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4 2xl:grid-cols-8">
                <CompactStat
                  label="Track Type"
                  value={selectedMeta ? getTrackType(selectedMeta).replace(/_/g, " ") : "—"}
                  sub={selectedMeta?.size_group ? `Size group: ${selectedMeta.size_group}` : ""}
                />
                <CompactStat
                  label="Track Position Dependency"
                  value={behaviorSummary ? fmt(behaviorSummary.track_position_dependency, 3) : "—"}
                  sub={quickRead?.tp || ""}
                />
                <CompactStat
                  label="Chaos Score"
                  value={behaviorSummary ? fmt(behaviorSummary.chaos_score, 3) : "—"}
                  sub={quickRead?.chaosLabel || ""}
                />
                <CompactStat
                  label="Dominator Shape"
                  value={behaviorSummary ? fmt(behaviorSummary.dominator_concentration, 3) : "—"}
                  sub={quickRead?.domLabel || ""}
                />
                <CompactStat
                  label="Best Comp"
                  value={similarTracks[0]?.track || "—"}
                  sub={similarTracks[0]?.similarity != null ? `Score ${fmt(similarTracks[0].similarity, 3)}` : ""}
                />
                <CompactStat
                  label="Avg Cautions"
                  value={raceIncidentSummary.avg_cautions != null ? fmt(raceIncidentSummary.avg_cautions, 1) : "—"}
                  sub={
                    raceIncidentSummary.caution_races
                      ? `Per race • ${
                          raceIncidentPercentiles.cautions != null
                            ? `${ordinalPct(raceIncidentPercentiles.cautions)} percentile`
                            : "Percentile N/A"
                        }`
                      : "No caution data"
                  }
                />
                <CompactStat
                  label="Avg DNFs"
                  value={raceIncidentSummary.avg_dnfs != null ? fmt(raceIncidentSummary.avg_dnfs, 1) : "—"}
                  sub={
                    raceIncidentSummary.dnf_races
                      ? `Per race • ${
                          raceIncidentPercentiles.dnfs != null
                            ? `${ordinalPct(raceIncidentPercentiles.dnfs)} percentile`
                            : "Percentile N/A"
                        }`
                      : "No DNF data"
                  }
                />
                <CompactStat
                  label="Races / Rows"
                  value={behaviorSummary ? `${fmt(behaviorSummary.races_used)} / ${fmt(behaviorSummary.rows_used)}` : "—"}
                  sub="Races used / driver rows"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <Section title="Track Profile">
                  {selectedMeta ? (
                    <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                      <div>
                        <DataPoint label="Track" value={selectedMeta.track} />
                        <DataPoint label="Shape" value={fmt(selectedMeta.shape)} />
                        <DataPoint label="Length" value={fmt(selectedMeta.length_miles, 3)} />
                        <DataPoint label="T1 Banking" value={toNum(selectedMeta.turn1_banking_deg) != null ? `${fmt(selectedMeta.turn1_banking_deg, 1)}°` : "—"} />
                        <DataPoint label="Frontstretch" value={fmt(selectedMeta.frontstretch_ft)} />
                        <DataPoint label="Width" value={fmt(selectedMeta.track_width_ft)} />
                        <DataPoint label="High Tire Wear" value={prettyBool(selectedMeta.is_high_tire_wear)} />
                        <DataPoint label="Street Course" value={prettyBool(selectedMeta.is_street_course)} />
                      </div>
                      <div>
                        <DataPoint label="Surface" value={fmt(selectedMeta.surface)} />
                        <DataPoint label="Size Group" value={fmt(selectedMeta.size_group)} />
                        <DataPoint label="Banking" value={banking.text} />
                        <DataPoint label="T3 Banking" value={toNum(selectedMeta.turn3_banking_deg) != null ? `${fmt(selectedMeta.turn3_banking_deg, 1)}°` : "—"} />
                        <DataPoint label="Backstretch" value={fmt(selectedMeta.backstretch_ft)} />
                        <DataPoint label="Concrete" value={prettyBool(selectedMeta.is_concrete)} />
                        <DataPoint label="Road Course" value={prettyBool(selectedMeta.is_road_course)} />
                        <DataPoint label="Last Repave" value={fmt(selectedMeta.last_repave)} />
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-400">No track metadata found.</div>
                  )}

                  {selectedMeta?.custom_groups?.length ? (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {selectedMeta.custom_groups.map((g) => (
                        <span key={g} className="border border-slate-700 bg-slate-900 px-2 py-1 text-[11px] text-slate-300">
                          {g}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </Section>

                <Section title="Behavior Profile">
                  {behaviorSummary ? (
                    <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                      <div>
                        <DataPoint label="Races Used" value={fmt(behaviorSummary.races_used)} />
                        <DataPoint label="Start / Finish Corr" value={fmt(behaviorSummary.start_finish_corr, 3)} />
                        <DataPoint label="Winner from Top 5" value={fmtPct(behaviorSummary.winner_from_top5_start_pct)} />
                        <DataPoint label="Dominator Concentration" value={fmt(behaviorSummary.dominator_concentration, 3)} />
                        <DataPoint label="Finish Volatility" value={fmt(behaviorSummary.finish_volatility, 2)} />
                        <DataPoint label="Chaos Score" value={fmt(behaviorSummary.chaos_score, 3)} />
                        <DataPoint label="Avg GFPD" value={fmt(behaviorSummary.avg_green_flag_passing_diff, 2)} />
                        <DataPoint label="Top 5 LL Share" value={fmtPct(behaviorSummary.top5_laps_led_share)} />
                      </div>
                      <div>
                        <DataPoint label="Rows Used" value={fmt(behaviorSummary.rows_used)} />
                        <DataPoint label="Avg Abs PD" value={fmt(behaviorSummary.avg_abs_pd, 2)} />
                        <DataPoint label="Top 10 Carry Rate" value={fmtPct(behaviorSummary.top10_start_to_top10_finish_pct)} />
                        <DataPoint label="Fast Lap Concentration" value={fmt(behaviorSummary.fast_lap_concentration, 3)} />
                        <DataPoint label="Track Position Dependency" value={fmt(behaviorSummary.track_position_dependency, 3)} />
                        <DataPoint label="Avg Rating" value={fmt(behaviorSummary.avg_rating, 2)} />
                        <DataPoint label="Avg Quality Passes" value={fmt(behaviorSummary.avg_quality_passes, 2)} />
                        <DataPoint label="Top 5 FL Share" value={fmtPct(behaviorSummary.top5_fast_lap_share)} />
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-400">No matching race-log data.</div>
                  )}
                </Section>

                <Section title="DFS Scoring Profile">
                  {behaviorSummary ? (
                    <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                      <div>
                        <DataPoint label="Avg DK Score" value={hasDk ? fmt(behaviorSummary.avg_dk, 2) : "—"} />
                        <DataPoint label="Median DK Score" value={hasDk ? fmt(behaviorSummary.median_dk, 2) : "—"} />
                        <DataPoint label="DK Std Dev" value={hasDk ? fmt(behaviorSummary.dk_std, 2) : "—"} />
                        <DataPoint label="Best DK Score" value={hasDk ? fmt(behaviorSummary.max_dk, 2) : "—"} />
                      </div>
                      <div>
                        <DataPoint label="Avg FD Score" value={hasFd ? fmt(behaviorSummary.avg_fd, 2) : "—"} />
                        <DataPoint label="Median FD Score" value={hasFd ? fmt(behaviorSummary.median_fd, 2) : "—"} />
                        <DataPoint label="FD Std Dev" value={hasFd ? fmt(behaviorSummary.fd_std, 2) : "—"} />
                        <DataPoint label="Best FD Score" value={hasFd ? fmt(behaviorSummary.max_fd, 2) : "—"} />
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-400">No fantasy scoring data for this filter.</div>
                  )}
                </Section>

                <Section title="Starter Share Profile">
                  {behaviorSummary ? (
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Laps Led Share by Start Bucket
                        </div>
                        <div className="grid grid-cols-3 gap-0 border border-slate-700">
                          <CompactStat label="Front Row" value={fmtPct(behaviorSummary.front_row_laps_led_share)} />
                          <CompactStat label="Top 5" value={fmtPct(behaviorSummary.top5_laps_led_share)} />
                          <CompactStat label="Top 10" value={fmtPct(behaviorSummary.top10_laps_led_share)} />
                        </div>
                      </div>

                      <div>
                        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Fast Lap Share by Start Bucket
                        </div>
                        <div className="grid grid-cols-3 gap-0 border border-slate-700">
                          <CompactStat label="Front Row" value={fmtPct(behaviorSummary.front_row_fast_lap_share)} />
                          <CompactStat label="Top 5" value={fmtPct(behaviorSummary.top5_fast_lap_share)} />
                          <CompactStat label="Top 10" value={fmtPct(behaviorSummary.top10_fast_lap_share)} />
                        </div>
                      </div>

                      <div className="border border-slate-700 bg-slate-900 p-3 text-xs">
                        <div className="mb-1">
                          <span className="text-slate-500">Track position read:</span>{" "}
                          <span className="font-medium text-slate-100">{quickRead?.tp || "—"}</span>
                        </div>
                        <div className="mb-1">
                          <span className="text-slate-500">Chaos read:</span>{" "}
                          <span className="font-medium text-slate-100">{quickRead?.chaosLabel || "—"}</span>
                        </div>
                        <div className="mb-1">
                          <span className="text-slate-500">Dominator read:</span>{" "}
                          <span className="font-medium text-slate-100">{quickRead?.domLabel || "—"}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Best comp track:</span>{" "}
                          <span className="font-medium text-slate-100">{similarTracks[0]?.track || "—"}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-400">Not enough data yet.</div>
                  )}
                </Section>
              </div>

              {mainView === "charts" ? (
                <div className="grid grid-cols-1 gap-0">
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <Section
                      title="Interactive Scatter / Correlation"
                      right={scatterCorr != null ? `r = ${fmt(scatterCorr, 3)} • rows ${scatterPoints.length}` : `rows ${scatterPoints.length}`}
                    >
                      <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <MetricSelect label="X Axis" value={scatterX} onChange={setScatterX} options={scatterMetricOptions} />
                        <MetricSelect label="Y Axis" value={scatterY} onChange={setScatterY} options={scatterMetricOptions} />
                      </div>

                      <div className="h-[420px] rounded-xl border border-slate-800 bg-slate-900 p-2">
                        <ResponsiveContainer width="100%" height="100%">
                          <ScatterChart margin={{ top: 12, right: 18, left: 4, bottom: 18 }}>
                            <CartesianGrid stroke="rgba(148,163,184,0.15)" />
                            <XAxis
                              type="number"
                              dataKey="x"
                              stroke="rgba(148,163,184,0.9)"
                              tick={{ fill: "#cbd5e1", fontSize: 11 }}
                              name={scatterMetricOptions.find((o) => o.value === scatterX)?.label || "X"}
                            />
                            <YAxis
                              type="number"
                              dataKey="y"
                              stroke="rgba(148,163,184,0.9)"
                              tick={{ fill: "#cbd5e1", fontSize: 11 }}
                              name={scatterMetricOptions.find((o) => o.value === scatterY)?.label || "Y"}
                            />
                            {scatterMeans.x != null ? <ReferenceLine x={scatterMeans.x} stroke="rgba(56,189,248,0.45)" strokeDasharray="5 5" /> : null}
                            {scatterMeans.y != null ? <ReferenceLine y={scatterMeans.y} stroke="rgba(56,189,248,0.45)" strokeDasharray="5 5" /> : null}
                            {scatterRegressionLine.length === 2 ? (
                              <Line
                                type="linear"
                                dataKey="y"
                                data={scatterRegressionLine}
                                dot={false}
                                stroke="rgba(251,191,36,0.9)"
                                strokeWidth={2}
                                isAnimationActive={false}
                              />
                            ) : null}
                            <Tooltip
                              content={
                                <RechartsTooltip
                                  labelFormatter={(_, payload) => {
                                    const p = payload?.[0]?.payload;
                                    return p ? `${p.driver} • ${p.date}${p.race ? ` • ${p.race}` : ""}` : "";
                                  }}
                                  formatter={(value, name, entry) => {
                                    return fmt(value, 2);
                                  }}
                                />
                              }
                            />
                            <Scatter data={scatterPoints} name="Point" fill="rgba(56,189,248,0.9)">
                              {scatterPoints.map((p) => {
                                const bucket = p.startBucket;
                                let fill = "rgba(56,189,248,0.9)";
                                if (bucket === "1-2") fill = "rgba(239,68,68,0.9)";
                                else if (bucket === "3-5") fill = "rgba(249,115,22,0.9)";
                                else if (bucket === "6-10") fill = "rgba(250,204,21,0.9)";
                                else if (bucket === "11-15") fill = "rgba(34,197,94,0.9)";
                                else if (bucket === "16-20") fill = "rgba(59,130,246,0.9)";
                                else if (bucket === "21-25") fill = "rgba(168,85,247,0.9)";
                                return <Cell key={p.id} fill={fill} />;
                              })}
                            </Scatter>
                          </ScatterChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="mt-2 text-[11px] text-slate-400">
                        Dots are colored by starting-position bucket. Dashed lines show the averages.
                      </div>
                    </Section>

                    <Section title="Histogram / Distribution" right={`mean ${fmt(histogramMeanMedian.mean, 2)} • median ${fmt(histogramMeanMedian.median, 2)}`}>
                      <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-[1fr_120px]">
                        <MetricSelect
                          label="Metric"
                          value={histMetric}
                          onChange={setHistMetric}
                          options={[
                            ...(hasDk ? [{ value: "dk", label: "DK Score" }] : []),
                            ...(hasFd ? [{ value: "fd", label: "FD Score" }] : []),
                            { value: "finish", label: "Finish" },
                            { value: "pd", label: "PD" },
                            { value: "laps_led", label: "Laps Led" },
                            { value: "fast_laps", label: "Fast Laps" },
                          ]}
                        />
                        <div className="flex flex-col">
                          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Bins</div>
                          <input
                            type="number"
                            min={4}
                            max={30}
                            value={histBins}
                            onChange={(e) => setHistBins(Number(e.target.value) || 12)}
                            className="h-8 min-w-0 rounded-md border border-slate-700 bg-slate-900 px-2 text-xs text-slate-100 outline-none focus:border-slate-500"
                          />
                        </div>
                      </div>

                      <div className="h-[420px] rounded-xl border border-slate-800 bg-slate-900 p-2">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={histogramData} margin={{ top: 12, right: 18, left: 0, bottom: 50 }}>
                            <CartesianGrid stroke="rgba(148,163,184,0.15)" vertical={false} />
                            <XAxis
                              dataKey="binLabel"
                              angle={-35}
                              textAnchor="end"
                              height={70}
                              stroke="rgba(148,163,184,0.9)"
                              tick={{ fill: "#cbd5e1", fontSize: 10 }}
                            />
                            <YAxis stroke="rgba(148,163,184,0.9)" tick={{ fill: "#cbd5e1", fontSize: 11 }} />
                            <Tooltip content={<RechartsTooltip formatter={(value) => fmt(value, 0)} />} />
                            <Bar dataKey="count" name="Count" fill="rgba(56,189,248,0.85)" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </Section>
                  </div>

                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <Section
                      title="Average by Exact Starting Position"
                      right={startPosMetricOptions.find((o) => o.value === startPosMetric)?.label || ""}
                    >
                      <div className="mb-3">
                        <MetricSelect
                          label="Metric"
                          value={startPosMetric}
                          onChange={setStartPosMetric}
                          options={startPosMetricOptions}
                          className="max-w-[260px]"
                        />
                      </div>

                      <div className="h-[400px] rounded-xl border border-slate-800 bg-slate-900 p-2">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={startPositionData} margin={{ top: 12, right: 18, left: 0, bottom: 18 }}>
                            <CartesianGrid stroke="rgba(148,163,184,0.15)" />
                            <XAxis
                              dataKey="start"
                              stroke="rgba(148,163,184,0.9)"
                              tick={{ fill: "#cbd5e1", fontSize: 11 }}
                            />
                            <YAxis
                              stroke="rgba(148,163,184,0.9)"
                              tick={{ fill: "#cbd5e1", fontSize: 11 }}
                              tickFormatter={(v) => (startPosMetricIsPct ? `${Math.round(v * 100)}%` : fmt(v, 1))}
                            />
                            <Tooltip
                              content={
                                <RechartsTooltip
                                  labelFormatter={(label, payload) => {
                                    const p = payload?.[0]?.payload;
                                    return p ? `Start ${p.start} • ${p.count} races` : `Start ${label}`;
                                  }}
                                  formatter={(value) => (startPosMetricIsPct ? fmtPct(value, 1) : fmt(value, 2))}
                                />
                              }
                            />
                            <Line
                              type="monotone"
                              dataKey={startPosMetric}
                              name="Value"
                              stroke="rgba(34,197,94,0.9)"
                              strokeWidth={2.5}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </Section>

                    <Section title="Race Trend Over Time" right={trendMetricOptions.find((o) => o.value === trendMetric)?.label || ""}>
                      <div className="mb-3">
                        <MetricSelect label="Metric" value={trendMetric} onChange={setTrendMetric} options={trendMetricOptions} className="max-w-[260px]" />
                      </div>

                      <div className="h-[400px] rounded-xl border border-slate-800 bg-slate-900 p-2">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={trendData} margin={{ top: 12, right: 18, left: 0, bottom: 18 }}>
                            <CartesianGrid stroke="rgba(148,163,184,0.15)" />
                            <XAxis
                              dataKey="date"
                              stroke="rgba(148,163,184,0.9)"
                              tick={{ fill: "#cbd5e1", fontSize: 11 }}
                            />
                            <YAxis
                              stroke="rgba(148,163,184,0.9)"
                              tick={{ fill: "#cbd5e1", fontSize: 11 }}
                              tickFormatter={(v) => (trendMetricIsPct ? `${Math.round(v * 100)}%` : fmt(v, 1))}
                            />
                            {trendAvg != null ? <ReferenceLine y={trendAvg} stroke="rgba(251,191,36,0.6)" strokeDasharray="5 5" /> : null}
                            <Tooltip
                              content={
                                <RechartsTooltip
                                  labelFormatter={(_, payload) => payload?.[0]?.payload?.label || ""}
                                  formatter={(value) => (trendMetricIsPct ? fmtPct(value, 1) : fmt(value, 2))}
                                />
                              }
                            />
                            <Line
                              type="monotone"
                              dataKey={trendMetric}
                              name="Value"
                              stroke="rgba(168,85,247,0.9)"
                              strokeWidth={2.5}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </Section>
                  </div>

                  <div className="min-w-0">
                    <Section
                      title="Track Analytics"
                      right={analyticsChartMode === "ranking" ? "Leaderboard view" : `${track || "Current track"} relationship view`}
                    >
                      <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
                        <div className="inline-flex w-fit overflow-hidden border border-slate-700 bg-slate-950">
                          <button
                            type="button"
                            onClick={() => setAnalyticsChartMode("ranking")}
                            className={`px-3 py-1.5 text-xs font-medium ${analyticsChartMode === "ranking" ? "border-sky-400 text-white" : "border-transparent text-slate-400 hover:text-white"}`}
                          >
                            Track Leaderboard
                          </button>
                          <button
                            type="button"
                            onClick={() => setAnalyticsChartMode("correlation")}
                            className={`border-l border-slate-700 px-3 py-1.5 text-xs font-medium ${analyticsChartMode === "correlation" ? "border-sky-400 text-white" : "border-transparent text-slate-400 hover:text-white"}`}
                          >
                            Selected Track Scatter
                          </button>
                        </div>

                        {analyticsChartMode === "ranking" ? (
                          <div className="grid w-full grid-cols-1 gap-3 xl:max-w-[640px] md:grid-cols-[1fr_150px]">
                            <MetricSelect label="Metric" value={rankingMetric} onChange={setRankingMetric} options={rankingMetricOptions} />
                            <MetricSelect
                              label="Order"
                              value={rankingOrder}
                              onChange={setRankingOrder}
                              options={[
                                { value: "desc", label: "Highest" },
                                { value: "asc", label: "Lowest" },
                              ]}
                            />
                          </div>
                        ) : (
                          <div className="grid w-full grid-cols-1 gap-3 xl:max-w-[720px] md:grid-cols-2">
                            <MetricSelect label="X-Axis" value={scatterX} onChange={setScatterX} options={scatterMetricOptions} />
                            <MetricSelect label="Y-Axis" value={scatterY} onChange={setScatterY} options={scatterMetricOptions} />
                          </div>
                        )}
                      </div>

                      {analyticsChartMode === "ranking" ? (
                        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_320px]">
                          <GridTable>
                            <thead>
                              <tr>
                                <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">#</th>
                                <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-300">Track</th>
                                <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">Type</th>
                                <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">Races</th>
                                <th className="border-b border-slate-700 bg-slate-900 px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-300">
                                  {rankingMetricOptions.find((o) => o.value === rankingMetric)?.label || "Metric"}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedTrackAnalyticsRows.map((r, i) => {
                                const width = `${Math.max(6, ((r[rankingMetric] - rankingMetricRange.min) / rankingMetricRange.span) * 92 + 8)}%`;
                                return (
                                  <tr key={`${rankingMetric}-${r.track}`} className={i % 2 ? "bg-slate-950" : "bg-slate-900/30"}>
                                    <td className="border-b border-r border-slate-800 px-2 py-2 text-center text-slate-400">{i + 1}</td>
                                    <td className="border-b border-r border-slate-800 px-2 py-2 font-medium text-slate-100">{r.track}</td>
                                    <td className="border-b border-r border-slate-800 px-2 py-2 text-center text-slate-300">{String(r.track_type || "—").replace(/_/g, " ")}</td>
                                    <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(r.races_used, 0)}</td>
                                    <td className="border-b border-slate-800 px-2 py-2">
                                      <div className="flex items-center gap-3">
                                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-800">
                                          <div className="h-full rounded-full bg-sky-500/70" style={{ width }} />
                                        </div>
                                        <div className="w-16 text-right font-semibold text-slate-100">{formatRankingValue(r[rankingMetric])}</div>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </GridTable>

                          <div className="grid grid-cols-2 gap-2 xl:grid-cols-1">
                            <CompactStat label="Tracks" value={fmt(trackAnalyticsRows.length, 0)} sub="With usable data" />
                            <CompactStat label="Selected Metric" value={rankingMetricOptions.find((o) => o.value === rankingMetric)?.label || "—"} sub={rankingOrder === "desc" ? "Highest first" : "Lowest first"} />
                            <CompactStat label="Top Value" value={sortedTrackAnalyticsRows[0] ? formatRankingValue(sortedTrackAnalyticsRows[0][rankingMetric]) : "—"} sub={sortedTrackAnalyticsRows[0]?.track || "—"} />
                            <CompactStat label="Sample Filter" value={`${series}${years.length ? ` • ${years.join(", ")}` : ""}`} sub={excludeExhibition ? "Exhibitions excluded" : "Exhibitions included"} />
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_380px]">
                          <div className="h-[430px] rounded-xl border border-slate-800 bg-slate-900 p-2">
                            <ResponsiveContainer width="100%" height="100%">
                              <ScatterChart margin={{ top: 14, right: 22, left: 8, bottom: 28 }}>
                                <CartesianGrid stroke="rgba(148,163,184,0.15)" />
                                <XAxis
                                  type="number"
                                  dataKey="x"
                                  name={scatterMetricOptions.find((o) => o.value === scatterX)?.label || "X"}
                                  stroke="rgba(148,163,184,0.9)"
                                  tick={{ fill: "#cbd5e1", fontSize: 11 }}
                                  tickFormatter={(v) => fmt(v, 1)}
                                />
                                <YAxis
                                  type="number"
                                  dataKey="y"
                                  name={scatterMetricOptions.find((o) => o.value === scatterY)?.label || "Y"}
                                  stroke="rgba(148,163,184,0.9)"
                                  tick={{ fill: "#cbd5e1", fontSize: 11 }}
                                  tickFormatter={(v) => fmt(v, 1)}
                                />
                                {scatterMeans.x != null ? <ReferenceLine x={scatterMeans.x} stroke="rgba(148,163,184,0.35)" strokeDasharray="4 4" /> : null}
                                {scatterMeans.y != null ? <ReferenceLine y={scatterMeans.y} stroke="rgba(148,163,184,0.35)" strokeDasharray="4 4" /> : null}
                                <Tooltip
                                  content={
                                    <RechartsTooltip
                                      labelFormatter={(_, payload) => {
                                        const p = payload?.[0]?.payload;
                                        return p ? `${p.driver} • ${p.date}${p.race ? ` • ${p.race}` : ""}` : "";
                                      }}
                                      formatter={(value, name) => fmt(value, 2)}
                                    />
                                  }
                                />
                                <Scatter data={scatterPoints} name="Driver race logs" fill="rgba(56,189,248,0.72)" />
                                {scatterRegressionLine.length ? (
                                  <Line
                                    type="linear"
                                    data={scatterRegressionLine}
                                    dataKey="y"
                                    name="Trend"
                                    stroke="rgba(251,191,36,0.95)"
                                    strokeWidth={2}
                                    dot={false}
                                    isAnimationActive={false}
                                  />
                                ) : null}
                              </ScatterChart>
                            </ResponsiveContainer>
                          </div>

                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                              <CompactStat label="Correlation" value={fmt(scatterCorr, 3)} sub={`${scatterMetricOptions.find((o) => o.value === scatterX)?.label || "X"} → ${scatterMetricOptions.find((o) => o.value === scatterY)?.label || "Y"}`} />
                              <CompactStat label="R²" value={fmtPct(r2FromCorr(scatterCorr), 1)} sub="Explained relationship" />
                              <CompactStat label="Points" value={fmt(scatterPoints.length, 0)} sub="Driver race logs" />
                              <CompactStat label="Mean X / Y" value={`${fmt(scatterMeans.x, 1)} / ${fmt(scatterMeans.y, 1)}`} sub="Dashed guide lines" />
                            </div>

                            <GridTable>
                              <thead>
                                <tr>
                                  <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-300">Relationship</th>
                                  <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">Corr</th>
                                  <th className="border-b border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">R²</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedCorrelationRows.map((r, i) => (
                                  <tr key={r.label} className={i % 2 ? "bg-slate-950" : "bg-slate-900/30"}>
                                    <td className="border-b border-r border-slate-800 px-2 py-2 font-medium text-slate-100">{r.label}</td>
                                    <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(r.corr, 3)}</td>
                                    <td className="border-b border-slate-800 px-2 py-2 text-center">{fmtPct(r.r2, 1)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </GridTable>

                            <div className="text-[11px] leading-relaxed text-slate-400">
                              Use this view for the stuff that actually needs a plot: start spot vs finish, start vs dominator points, finish vs DK/FD, PD vs fantasy points, and any other pair from the dropdowns.
                            </div>
                          </div>
                        </div>
                      )}
                    </Section>
                  </div>
                </div>
              ) : (
                <>

                  <div className="min-w-0">
                    <Section title="Track Analytics Top 10" right={`${rankingMetricOptions.find((o) => o.value === rankingMetric)?.label || "Metric"} • ${rankingOrder === "desc" ? "Highest" : "Lowest"}`}>
                      <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-[1fr_150px] xl:max-w-[650px]">
                        <MetricSelect label="Metric" value={rankingMetric} onChange={setRankingMetric} options={rankingMetricOptions} />
                        <MetricSelect
                          label="Order"
                          value={rankingOrder}
                          onChange={setRankingOrder}
                          options={[
                            { value: "desc", label: "Highest" },
                            { value: "asc", label: "Lowest" },
                          ]}
                        />
                      </div>

                      <GridTable>
                        <thead>
                          <tr>
                            <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">#</th>
                            <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-300">Track</th>
                            <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">Type</th>
                            <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">Races</th>
                            <th className="border-b border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedTrackAnalyticsRows.map((r, i) => (
                            <tr key={`top10-${rankingMetric}-${r.track}`} className={i % 2 ? "bg-slate-950" : "bg-slate-900/30"}>
                              <td className="border-b border-r border-slate-800 px-2 py-2 text-center text-slate-400">{i + 1}</td>
                              <td className="border-b border-r border-slate-800 px-2 py-2 font-medium text-slate-100">{r.track}</td>
                              <td className="border-b border-r border-slate-800 px-2 py-2 text-center text-slate-300">{String(r.track_type || "—").replace(/_/g, " ")}</td>
                              <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(r.races_used, 0)}</td>
                              <td className="border-b border-slate-800 px-2 py-2 text-center font-semibold text-slate-100">{formatRankingValue(r[rankingMetric])}</td>
                            </tr>
                          ))}
                        </tbody>
                      </GridTable>
                    </Section>
                  </div>

                  <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
                    <Section title="Most Similar Tracks" right="Type-aware + behavior-aware comps">
                      <GridTable>
                        <thead>
                          <tr>
                            <SortHeader label="Track" sortKey="track" activeSortKey={similarSort.key} sortDir={similarSort.dir} onSort={(key) => handleSort(setSimilarSort, key)} />
                            <SortHeader label="Score" sortKey="similarity" activeSortKey={similarSort.key} sortDir={similarSort.dir} onSort={(key) => handleSort(setSimilarSort, key)} align="center" />
                            <SortHeader label="Type" sortKey="track_type" activeSortKey={similarSort.key} sortDir={similarSort.dir} onSort={(key) => handleSort(setSimilarSort, key)} align="center" />
                            <SortHeader label="Groups" sortKey="custom_groups" activeSortKey={similarSort.key} sortDir={similarSort.dir} onSort={(key) => handleSort(setSimilarSort, key)} align="center" />
                            <SortHeader label="Length" sortKey="length_miles" activeSortKey={similarSort.key} sortDir={similarSort.dir} onSort={(key) => handleSort(setSimilarSort, key)} align="center" />
                            <SortHeader label="Banking" sortKey="banking_avg" activeSortKey={similarSort.key} sortDir={similarSort.dir} onSort={(key) => handleSort(setSimilarSort, key)} align="center" />
                            <SortHeader label="TPD" sortKey="track_position_dependency" activeSortKey={similarSort.key} sortDir={similarSort.dir} onSort={(key) => handleSort(setSimilarSort, key)} align="center" />
                            <SortHeader label="Chaos" sortKey="chaos_score" activeSortKey={similarSort.key} sortDir={similarSort.dir} onSort={(key) => handleSort(setSimilarSort, key)} align="center" />
                          </tr>
                        </thead>
                        <tbody>
                          {similarTracks.length ? (
                            similarTracks.map((r, i) => (
                              <tr key={`${r.track}-${i}`} className={i % 2 ? "bg-slate-950" : "bg-slate-900/30"}>
                                <td className="border-b border-r border-slate-800 px-2 py-2 font-medium text-slate-100">{r.track}</td>
                                <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(r.similarity, 3)}</td>
                                <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{r.track_type.replace(/_/g, " ")}</td>
                                <td className="border-b border-r border-slate-800 px-2 py-2 text-center text-slate-300">{r.custom_groups || "—"}</td>
                                <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(r.length_miles, 3)}</td>
                                <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{r.banking_avg != null ? `${fmt(r.banking_avg, 1)}°` : "—"}</td>
                                <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(r.track_position_dependency, 3)}</td>
                                <td className="border-b border-slate-800 px-2 py-2 text-center">{fmt(r.chaos_score, 3)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td className="px-2 py-4 text-center text-slate-400" colSpan={8}>
                                No similar tracks found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </GridTable>
                    </Section>

                    <Section title="Starting Position Analysis" right="Now includes DK / FD scoring by bucket when available">
                      <GridTable>
                        <thead>
                          <tr>
                            <SortHeader label="Bucket" sortKey="sort_order" activeSortKey={bucketSort.key} sortDir={bucketSort.dir} onSort={(key) => handleSort(setBucketSort, key, "asc")} />
                            <SortHeader label="Rows" sortKey="count" activeSortKey={bucketSort.key} sortDir={bucketSort.dir} onSort={(key) => handleSort(setBucketSort, key)} align="center" />
                            <SortHeader label="Avg Finish" sortKey="avg_finish" activeSortKey={bucketSort.key} sortDir={bucketSort.dir} onSort={(key) => handleSort(setBucketSort, key)} align="center" />
                            <SortHeader label="Avg PD" sortKey="avg_pd" activeSortKey={bucketSort.key} sortDir={bucketSort.dir} onSort={(key) => handleSort(setBucketSort, key)} align="center" />
                            <SortHeader label="Win %" sortKey="win_pct" activeSortKey={bucketSort.key} sortDir={bucketSort.dir} onSort={(key) => handleSort(setBucketSort, key)} align="center" />
                            <SortHeader label="Top 5 %" sortKey="top5_pct" activeSortKey={bucketSort.key} sortDir={bucketSort.dir} onSort={(key) => handleSort(setBucketSort, key)} align="center" />
                            <SortHeader label="Top 10 %" sortKey="top10_pct" activeSortKey={bucketSort.key} sortDir={bucketSort.dir} onSort={(key) => handleSort(setBucketSort, key)} align="center" />
                            {hasDk ? (
                              <SortHeader label="Avg DK" sortKey="avg_dk" activeSortKey={bucketSort.key} sortDir={bucketSort.dir} onSort={(key) => handleSort(setBucketSort, key)} align="center" />
                            ) : null}
                            {hasFd ? (
                              <SortHeader label="Avg FD" sortKey="avg_fd" activeSortKey={bucketSort.key} sortDir={bucketSort.dir} onSort={(key) => handleSort(setBucketSort, key)} align="center" />
                            ) : null}
                            <SortHeader label="LL Share" sortKey="laps_led_share" activeSortKey={bucketSort.key} sortDir={bucketSort.dir} onSort={(key) => handleSort(setBucketSort, key)} align="center" />
                            <SortHeader label="FL Share" sortKey="fast_lap_share" activeSortKey={bucketSort.key} sortDir={bucketSort.dir} onSort={(key) => handleSort(setBucketSort, key)} align="center" />
                          </tr>
                        </thead>
                        <tbody>
                          {sortedStartBuckets.length ? (
                            sortedStartBuckets.map((r, i) => (
                              <tr key={`${r.bucket}-${i}`} className={i % 2 ? "bg-slate-950" : "bg-slate-900/30"}>
                                <td className="border-b border-r border-slate-800 px-2 py-2 font-medium text-slate-100">{r.bucket}</td>
                                <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(r.count)}</td>
                                <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(r.avg_finish, 2)}</td>
                                <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmtSigned(r.avg_pd, 2)}</td>
                                <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmtPct(r.win_pct)}</td>
                                <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmtPct(r.top5_pct)}</td>
                                <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmtPct(r.top10_pct)}</td>
                                {hasDk ? <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(r.avg_dk, 1)}</td> : null}
                                {hasFd ? <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(r.avg_fd, 1)}</td> : null}
                                <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmtPct(r.laps_led_share)}</td>
                                <td className="border-b border-slate-800 px-2 py-2 text-center">{fmtPct(r.fast_lap_share)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td className="px-2 py-4 text-center text-slate-400" colSpan={hasDk && hasFd ? 11 : hasDk || hasFd ? 10 : 9}>
                                No start bucket data.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </GridTable>
                    </Section>
                  </div>

                  <Section title="Historical Races At This Track" right="Click a row to expand race detail">
                    <GridTable>
                      <thead>
                        <tr>
                          <SortHeader label="Date" sortKey="date" activeSortKey={raceSort.key} sortDir={raceSort.dir} onSort={(key) => handleSort(setRaceSort, key, "desc")} />
                          <SortHeader label="Race" sortKey="race" activeSortKey={raceSort.key} sortDir={raceSort.dir} onSort={(key) => handleSort(setRaceSort, key)} />
                          <SortHeader label="Series" sortKey="series" activeSortKey={raceSort.key} sortDir={raceSort.dir} onSort={(key) => handleSort(setRaceSort, key)} align="center" />
                          <SortHeader label="Drivers" sortKey="drivers" activeSortKey={raceSort.key} sortDir={raceSort.dir} onSort={(key) => handleSort(setRaceSort, key)} align="center" />
                          <SortHeader label="Winner Start" sortKey="winner_start" activeSortKey={raceSort.key} sortDir={raceSort.dir} onSort={(key) => handleSort(setRaceSort, key)} align="center" />
                          {hasDk ? <SortHeader label="Avg DK" sortKey="avg_dk" activeSortKey={raceSort.key} sortDir={raceSort.dir} onSort={(key) => handleSort(setRaceSort, key)} align="center" /> : null}
                          {hasFd ? <SortHeader label="Avg FD" sortKey="avg_fd" activeSortKey={raceSort.key} sortDir={raceSort.dir} onSort={(key) => handleSort(setRaceSort, key)} align="center" /> : null}
                          <SortHeader label="Winner Top 5" sortKey="winner_started_top5" activeSortKey={raceSort.key} sortDir={raceSort.dir} onSort={(key) => handleSort(setRaceSort, key)} align="center" />
                          <SortHeader label="LL Leader Share" sortKey="ll_leader_share" activeSortKey={raceSort.key} sortDir={raceSort.dir} onSort={(key) => handleSort(setRaceSort, key)} align="center" />
                          <SortHeader label="FL Leader Share" sortKey="fl_leader_share" activeSortKey={raceSort.key} sortDir={raceSort.dir} onSort={(key) => handleSort(setRaceSort, key)} align="center" />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRacesForTrack.length ? (
                          sortedRacesForTrack.map((race, i) => {
                            const open = expandedRaceKey === race.race_key;
                            return (
                              <React.Fragment key={race.race_key}>
                                <tr
                                  className={`${i % 2 ? "bg-slate-950" : "bg-slate-900/30"} cursor-pointer hover:bg-slate-800/50`}
                                  onClick={() => setExpandedRaceKey((prev) => (prev === race.race_key ? "" : race.race_key))}
                                >
                                  <td className="border-b border-r border-slate-800 px-2 py-2 font-medium text-slate-100">{race.date}</td>
                                  <td className="border-b border-r border-slate-800 px-2 py-2">{race.race || "—"}</td>
                                  <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{race.series ? publicSeriesLabel(race.series) : "—"}</td>
                                  <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(race.drivers)}</td>
                                  <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(race.winner_start)}</td>
                                  {hasDk ? <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(race.avg_dk, 1)}</td> : null}
                                  {hasFd ? <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(race.avg_fd, 1)}</td> : null}
                                  <td className="border-b border-r border-slate-800 px-2 py-2 text-center">
                                    {race.winner_started_top5 == null ? "—" : race.winner_started_top5 ? "Yes" : "No"}
                                  </td>
                                  <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmtPct(race.ll_leader_share)}</td>
                                  <td className="border-b border-slate-800 px-2 py-2 text-center">{fmtPct(race.fl_leader_share)}</td>
                                </tr>

                                {open ? (
                                  <tr className="bg-slate-950">
                                    <td colSpan={hasDk && hasFd ? 10 : hasDk || hasFd ? 9 : 8} className="border-b border-slate-700 p-3">
                                      <div className="grid grid-cols-1 gap-3 xl:grid-cols-4">
                                        <Section title="Laps Led Share">
                                          <div className="grid grid-cols-3 gap-0 border border-slate-700">
                                            <CompactStat label="Front Row" value={fmtPct(race.front_row_laps_led_share)} />
                                            <CompactStat label="Top 5" value={fmtPct(race.top5_laps_led_share)} />
                                            <CompactStat label="Top 10" value={fmtPct(race.top10_laps_led_share)} />
                                          </div>
                                        </Section>

                                        <Section title="Fast Lap Share">
                                          <div className="grid grid-cols-3 gap-0 border border-slate-700">
                                            <CompactStat label="Front Row" value={fmtPct(race.front_row_fast_lap_share)} />
                                            <CompactStat label="Top 5" value={fmtPct(race.top5_fast_lap_share)} />
                                            <CompactStat label="Top 10" value={fmtPct(race.top10_fast_lap_share)} />
                                          </div>
                                        </Section>

                                        <Section title="Quick Race Read">
                                          <DataPoint label="Winner Start" value={fmt(race.winner_start)} />
                                          <DataPoint label="LL Leader Strength" value={rankPct(race.ll_leader_share, [0.35, 0.5, 0.65])} />
                                          <DataPoint label="FL Leader Strength" value={rankPct(race.fl_leader_share, [0.35, 0.5, 0.65])} />
                                        </Section>

                                        <Section title="DFS Race Summary">
                                          <DataPoint label="Avg DK" value={hasDk ? fmt(race.avg_dk, 2) : "—"} />
                                          <DataPoint label="Best DK" value={hasDk ? fmt(race.max_dk, 2) : "—"} />
                                          <DataPoint label="Avg FD" value={hasFd ? fmt(race.avg_fd, 2) : "—"} />
                                          <DataPoint label="Best FD" value={hasFd ? fmt(race.max_fd, 2) : "—"} />
                                        </Section>
                                      </div>

                                      <div className="mt-3">
                                        <GridTable>
                                          <thead>
                                            <tr>
                                              <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-300">Driver</th>
                                              <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">Start</th>
                                              <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">Finish</th>
                                              <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">PD</th>
                                              <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">Laps Led</th>
                                              <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">Fast Laps</th>
                                              {hasDk ? <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">DK</th> : null}
                                              {hasFd ? <th className="border-b border-r border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">FD</th> : null}
                                              <th className="border-b border-slate-700 bg-slate-900 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-300">Rating</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {[...race.rows]
                                              .sort((a, b) => {
                                                const af = toNum(logCols.finish ? a?.[logCols.finish] : null) ?? 999;
                                                const bf = toNum(logCols.finish ? b?.[logCols.finish] : null) ?? 999;
                                                return af - bf;
                                              })
                                              .map((r, idx) => {
                                                const s = toNum(logCols.start ? r?.[logCols.start] : null);
                                                const f = toNum(logCols.finish ? r?.[logCols.finish] : null);
                                                const pd = s != null && f != null ? s - f : null;
                                                return (
                                                  <tr key={`${race.race_key}-${idx}`} className={idx % 2 ? "bg-slate-950" : "bg-slate-900/30"}>
                                                    <td className="border-b border-r border-slate-800 px-2 py-2">{logCols.driver ? String(r?.[logCols.driver] ?? "—") : "—"}</td>
                                                    <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(s)}</td>
                                                    <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(f)}</td>
                                                    <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmtSigned(pd, 0)}</td>
                                                    <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(toNum(logCols.laps_led ? r?.[logCols.laps_led] : null), 0)}</td>
                                                    <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(toNum(logCols.fastest_lap ? r?.[logCols.fastest_lap] : null), 0)}</td>
                                                    {hasDk ? <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(getDkScore(r), 1)}</td> : null}
                                                    {hasFd ? <td className="border-b border-r border-slate-800 px-2 py-2 text-center">{fmt(getFdScore(r), 1)}</td> : null}
                                                    <td className="border-b border-slate-800 px-2 py-2 text-center">{fmt(toNum(logCols.rating ? r?.[logCols.rating] : null), 1)}</td>
                                                  </tr>
                                                );
                                              })}
                                          </tbody>
                                        </GridTable>
                                      </div>
                                    </td>
                                  </tr>
                                ) : null}
                              </React.Fragment>
                            );
                          })
                        ) : (
                          <tr>
                            <td className="px-2 py-4 text-center text-slate-400" colSpan={hasDk && hasFd ? 10 : hasDk || hasFd ? 9 : 8}>
                              No races found for this track.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </GridTable>
                  </Section>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}